import { Injectable } from '@angular/core';
import { scheduleMockData } from '../constants/onboarding/schedule';
import { TranslationPipe } from '../pipes/translation.pipe';

import {
  FinanceKpisData,
  FinanceSummaryData,
  FinanceSummaryRow,
  FinanceTrendsData,
  FinanceTrendsMonth,
  OperationsKpisData,
  MonthlyFinanceCategory,
  MonthlyFinanceItem,
  MonthlyFinanceMonthValue,
  MonthlyFinanceProfitAndLoss,
  MonthlyFinanceSubcategory,
} from 'src/models/monthly-finance.model';

export interface StrategySuggestion {
  icon: string;
  title: string;
  body: string;
  /** Drives the impact-pill color: positive = revenue gain, negative = cost cut. */
  impact: 'positive' | 'negative';
  metric: string;
}

export interface StrategyMockData {
  kpis: FinanceKpisData;
  trends: FinanceTrendsData;
  summary: FinanceSummaryData;
  suggestions: StrategySuggestion[];
  opsKpis: OperationsKpisData;
}

export type AccountType = 'regular' | 'rental' | 'company';

export interface RestockingItem {
  name: string;
  current: number;
  min: number;
  max: number;
}

export interface RoomDefaults {
  beds: number;
  baths: number;
  floors: number;
}

export interface PropertyInfoDefaults {
  name: string;
  summary: string;
  nextJob: string;
  lastJob: string;
}

@Injectable({
  providedIn: 'root'
})

export class OnboardingDummyDataService {
  
  private static readonly RESTOCKING_ITEMS: Record<AccountType, RestockingItem[]> = {
    regular: [
      { name: 'Toilet Paper', current: 24, min: 6, max: 36 },
      { name: 'Paper Towels', current: 12, min: 3, max: 18 },
      { name: 'Dish Soap', current: 2, min: 1, max: 3 },
      { name: 'Hand Soap', current: 4, min: 2, max: 6 },
      { name: 'Laundry Detergent', current: 1, min: 1, max: 2 },
      { name: 'Dishwasher Detergent', current: 1, min: 1, max: 2 },
      { name: 'Trash Bags', current: 30, min: 10, max: 50 },
      { name: 'All Purpose Cleaner', current: 2, min: 1, max: 3 },
      { name: 'Glass / Window Cleaner', current: 1, min: 1, max: 2 },
      { name: 'Bathroom Cleaner', current: 2, min: 1, max: 3 },
      { name: 'Sponge', current: 4, min: 2, max: 6 },
      { name: 'Air Freshener', current: 3, min: 1, max: 5 }
    ],
    rental: [
      { name: 'Toilet Paper', current: 40, min: 10, max: 60 },
      { name: 'Paper Towels', current: 20, min: 5, max: 30 },
      { name: 'Dish Soap', current: 3, min: 1, max: 5 },
      { name: 'Hand Soap', current: 6, min: 2, max: 8 },
      { name: 'Laundry Detergent', current: 40, min: 10, max: 60 },
      { name: 'Dishwasher Detergent', current: 30, min: 8, max: 45 },
      { name: 'Trash Bags', current: 40, min: 10, max: 60 },
      { name: 'Coffee', current: 5, min: 2, max: 8 },
      { name: 'Coffee Filters', current: 100, min: 20, max: 150 },
      { name: 'Shampoo', current: 6, min: 2, max: 10 },
      { name: 'Conditioner', current: 6, min: 2, max: 10 },
      { name: 'Body Wash', current: 6, min: 2, max: 10 }
    ],
    company: [
      { name: 'Toilet Paper', current: 48, min: 12, max: 72 },
      { name: 'Paper Towels', current: 24, min: 6, max: 36 },
      { name: 'Hand Soap', current: 12, min: 4, max: 18 },
      { name: 'Hand Sanitizer', current: 8, min: 2, max: 12 },
      { name: 'Trash Bags', current: 50, min: 15, max: 75 },
      { name: 'Coffee', current: 10, min: 3, max: 15 },
      { name: 'Coffee Filters', current: 200, min: 50, max: 300 },
      { name: 'Coffee Creamer', current: 20, min: 6, max: 30 },
      { name: 'Sugar', current: 5, min: 2, max: 8 },
      { name: 'Sweetener', current: 5, min: 2, max: 8 },
      { name: 'Napkin', current: 200, min: 50, max: 300 },
      { name: 'Disinfectant Wipe', current: 100, min: 25, max: 150 }
    ]
  };

  private static readonly ROOM_DEFAULTS: Record<AccountType, RoomDefaults> = {
    regular: {
      beds: 3,
      baths: 2,
      floors: 2
    },
    rental: {
      beds: 2,
      baths: 2,
      floors: 2
    },
    company: {
      beds: 0,
      baths: 2,
      floors: 1
    }
  };

  private static readonly PROPERTY_INFO_DEFAULTS: Record<AccountType, PropertyInfoDefaults> = {
    regular: {
      name: 'My Home',
      summary: 'Monitoring your home. Everything is running smoothly.',
      nextJob: 'No upcoming jobs',
      lastJob: 'No past jobs'
    },
    rental: {
      name: 'All Properties',
      summary: 'Monitoring all properties. Everything is running smoothly across your portfolio.',
      nextJob: 'No upcoming jobs',
      lastJob: 'No past jobs'
    },
    company: {
      name: 'All Locations',
      summary: 'Monitoring all office locations. Everything is running smoothly across your facilities.',
      nextJob: 'No upcoming jobs',
      lastJob: 'No past jobs'
    }
  };

  private static readonly DELIVERY_TIMING_PREFERENCES: Record<AccountType, string> = {
    regular: 'As needed',
    rental: 'Between guest stays',
    company: 'Weekly delivery'
  };

  private translationPipe = new TranslationPipe();

  constructor() {}

  getAccountType(): AccountType {
    const guestViewType = localStorage.getItem('guest_view_type');
    if (guestViewType === 'regular' || guestViewType === 'rental' || guestViewType === 'company') {
      return guestViewType;
    }
    const isRentalClient = localStorage.getItem('isRentalClient');
    if (isRentalClient === 'true') {
      return 'rental';
    }
    return 'regular';
  }

  getRestockingItems(accountType?: AccountType): RestockingItem[] {
    const type = accountType || this.getAccountType();
    return OnboardingDummyDataService.RESTOCKING_ITEMS[type].map(item => ({
      ...item,
      name: this.translationPipe.transform(item.name)
    }));
  }

  getRoomDefaults(accountType?: AccountType): RoomDefaults {
    const type = accountType || this.getAccountType();
    return { ...OnboardingDummyDataService.ROOM_DEFAULTS[type] };
  }

  getPropertyInfoDefaults(accountType?: AccountType): PropertyInfoDefaults {
    const type = accountType || this.getAccountType();
    const defaults = OnboardingDummyDataService.PROPERTY_INFO_DEFAULTS[type];
    return {
      name: this.translationPipe.transform(defaults.name),
      summary: this.translationPipe.transform(defaults.summary),
      nextJob: this.translationPipe.transform(defaults.nextJob),
      lastJob: this.translationPipe.transform(defaults.lastJob)
    };
  }

  getDeliveryTimingPreference(accountType?: AccountType): string {
    const type = accountType || this.getAccountType();
    return this.translationPipe.transform(OnboardingDummyDataService.DELIVERY_TIMING_PREFERENCES[type]);
  }

  getRoomLabel(accountType?: AccountType): string {
    const type = accountType || this.getAccountType();
    if (type === 'company') {
      return this.translationPipe.transform('Rooms');
    }
    return this.translationPipe.transform('Bedrooms');
  }

  getRoomLabelSingular(accountType?: AccountType): string {
    const type = accountType || this.getAccountType();
    if (type === 'company') {
      return this.translationPipe.transform('Room');
    }
    return this.translationPipe.transform('Bedroom');
  }

  private formatDate(date: Date): string {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  }

  private formatDateSlash(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${month}/${day}/${date.getFullYear()}`;
  }

  private addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  getScheduleMockData(accountType?: AccountType): any[] {
    const type = accountType || this.getAccountType();
    if (type === 'rental') {
      return scheduleMockData;
    }
    if (type === 'regular') {
      return this.getRegularScheduleMockData();
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const scheduleData: any[] = [];
    for (let i = -3; i <= 10; i++) {
      const date = this.addDays(today, i);
      const dateStr = this.formatDate(date);
      const dayData: any = { date: dateStr, jobs: [], reservations: [], availability_requests: [] };
      if (type === 'company') {
        dayData.jobs = this.generateCompanyJobs(date, i);
      }
      scheduleData.push(dayData);
    }
    return scheduleData;
  }

  private getRegularScheduleMockData(): any[] {
    return [
      { date: 'Mar 25, 2025', jobs: [], reservations: [], availability_requests: [] },
      { date: 'Mar 26, 2025', jobs: [
        {
          id: 3000001,
          date: 'Mar 26, 2025',
          address_id: 700001,
          service_name: '2.5 hour cleaning',
          service_category: 'regular_cleaning',
          service_icon_url: 'https://d30thx9uw6scot.cloudfront.net/svgs/regular_cleaning.svg',
          start_time: '10:00',
          timezone: 'America/Los_Angeles',
          is_private: true,
          job_progress: 'completed',
          is_job_request: false,
          booking: { id: 3000001, start_date_no_earlier_than: '03/26/2025', start_time_no_earlier_than: '10:00am', end_date_no_later_than: '03/26/2025', end_time_no_later_than: '1:00pm' },
          homekeeper_jobs: [{ id: 3001001, homekeeper: { id: 300001, name: 'Maria Garcia' } }]
        }
      ], reservations: [], availability_requests: [] },
      { date: 'Mar 27, 2025', jobs: [], reservations: [], availability_requests: [] },
      { date: 'Mar 28, 2025', jobs: [], reservations: [], availability_requests: [] },
      { date: 'Mar 29, 2025', jobs: [], reservations: [], availability_requests: [] },
      { date: 'Mar 30, 2025', jobs: [], reservations: [], availability_requests: [] },
      { date: 'Mar 31, 2025', jobs: [
        {
          id: 3000002,
          date: 'Mar 31, 2025',
          address_id: 700001,
          service_name: '2.5 hour cleaning',
          service_category: 'regular_cleaning',
          service_icon_url: 'https://d30thx9uw6scot.cloudfront.net/svgs/regular_cleaning.svg',
          start_time: '10:00',
          timezone: 'America/Los_Angeles',
          is_private: true,
          job_progress: 'scheduled',
          is_job_request: false,
          booking: { id: 3000002, start_date_no_earlier_than: '03/31/2025', start_time_no_earlier_than: '10:00am', end_date_no_later_than: '03/31/2025', end_time_no_later_than: '1:00pm' },
          homekeeper_jobs: [{ id: 3001002, homekeeper: { id: 300001, name: 'Maria Garcia' } }]
        }
      ], reservations: [], availability_requests: [] }
    ];
  }

  private generateRentalJobs(date: Date, dayOffset: number): any[] {
    const dateStr = this.formatDate(date);
    const dateSlash = this.formatDateSlash(date);
    const jobs: any[] = [];
    let jobId = 2000000 + dayOffset * 100;
    if (dayOffset === 0 || dayOffset === 3 || dayOffset === 7) {
      jobs.push({
        id: jobId++,
        date: dateStr,
        address_id: 623549,
        service_name: '2.5 hour cleaning',
        service_category: 'regular_cleaning',
        service_icon_url: 'https://d30thx9uw6scot.cloudfront.net/svgs/regular_cleaning.svg',
        start_time: '11:00',
        timezone: 'America/Los_Angeles',
        is_private: true,
        job_progress: dayOffset < 0 ? 'completed' : 'scheduled',
        is_job_request: false,
        booking: { id: jobId, start_date_no_earlier_than: dateSlash, start_time_no_earlier_than: '11:00am', end_date_no_later_than: dateSlash, end_time_no_later_than: '4:00pm' },
        homekeeper_jobs: [{ id: jobId + 1000, homekeeper: { id: 221879, name: 'Aurelia Ili' } }]
      });
      jobs.push({
        id: jobId++,
        date: dateStr,
        address_id: 623549,
        service_name: 'Laundry Pickup',
        service_category: 'laundry',
        service_icon_url: 'https://d30thx9uw6scot.cloudfront.net/svgs/laundry.svg',
        start_time: '11:00',
        timezone: 'America/Los_Angeles',
        is_private: true,
        job_progress: dayOffset < 0 ? 'completed' : 'scheduled',
        is_job_request: false,
        booking: { id: jobId, start_date_no_earlier_than: dateSlash, start_time_no_earlier_than: '11:00am', end_date_no_later_than: dateSlash, end_time_no_later_than: '4:00pm' },
        homekeeper_jobs: [{ id: jobId + 1000, homekeeper: { id: 230478, name: 'Lisa Daniels' } }]
      });
    }
    if (dayOffset === 1 || dayOffset === 5 || dayOffset === 9) {
      jobs.push({
        id: jobId++,
        date: dateStr,
        address_id: 623561,
        service_name: '2.5 hour cleaning',
        service_category: 'regular_cleaning',
        service_icon_url: 'https://d30thx9uw6scot.cloudfront.net/svgs/regular_cleaning.svg',
        start_time: '10:00',
        timezone: 'America/Los_Angeles',
        is_private: true,
        job_progress: dayOffset < 0 ? 'completed' : 'scheduled',
        is_job_request: false,
        booking: { id: jobId, start_date_no_earlier_than: dateSlash, start_time_no_earlier_than: '10:00am', end_date_no_later_than: dateSlash, end_time_no_later_than: '3:00pm' },
        homekeeper_jobs: [{ id: jobId + 1000, homekeeper: { id: 244940, name: 'Blueline Enterprises' } }]
      });
    }
    if (dayOffset % 2 === 0) {
      jobs.push({
        id: jobId++,
        date: dateStr,
        address_id: 623559,
        service_name: 'Trash Out',
        service_category: 'trash_service',
        service_icon_url: 'https://d30thx9uw6scot.cloudfront.net/svgs/trash_service.svg',
        start_time: '08:00',
        timezone: 'America/Los_Angeles',
        is_private: true,
        job_progress: dayOffset < 0 ? 'completed' : 'scheduled',
        is_job_request: false,
        booking: { id: jobId, start_date_no_earlier_than: dateSlash, start_time_no_earlier_than: '8:00am', end_date_no_later_than: dateSlash, end_time_no_later_than: '8:30am' },
        homekeeper_jobs: [{ id: jobId + 1000, homekeeper: { id: 244948, name: 'Laki Siaki' } }]
      });
    }
    return jobs;
  }

  private generateRentalReservations(date: Date, dayOffset: number): any[] {
    const reservations: any[] = [];
    let resId = 1900000 + dayOffset * 10;
    if (dayOffset === -2) {
      reservations.push({
        id: resId++,
        address_id: 623549,
        check_in_date: this.formatDate(this.addDays(date, -3)),
        check_out_date: this.formatDate(date),
        check_in_time: '16:00',
        check_out_time: '11:00',
        reservation_status: 'check_out',
        timezone: 'America/Los_Angeles',
        total_guests: 2,
        details: { total_guests: 2, total_adults: 2, total_children: 0, total_pets: 0 }
      });
    }
    if (dayOffset === 0) {
      reservations.push({
        id: resId++,
        address_id: 623549,
        check_in_date: this.formatDate(date),
        check_out_date: this.formatDate(this.addDays(date, 4)),
        check_in_time: '16:00',
        check_out_time: '11:00',
        reservation_status: 'check_in',
        timezone: 'America/Los_Angeles',
        total_guests: 4,
        details: { total_guests: 4, total_adults: 2, total_children: 2, total_pets: 0 }
      });
    }
    if (dayOffset === 1 || dayOffset === 2 || dayOffset === 3) {
      reservations.push({
        id: resId++,
        address_id: 623549,
        check_in_date: this.formatDate(this.addDays(date, -dayOffset)),
        check_out_date: this.formatDate(this.addDays(date, 4 - dayOffset)),
        check_in_time: '16:00',
        check_out_time: '11:00',
        reservation_status: 'stay',
        timezone: 'America/Los_Angeles',
        total_guests: 4,
        details: { total_guests: 4, total_adults: 2, total_children: 2, total_pets: 0 }
      });
    }
    if (dayOffset === 3) {
      reservations.push({
        id: resId++,
        address_id: 623561,
        check_in_date: this.formatDate(date),
        check_out_date: this.formatDate(this.addDays(date, 5)),
        check_in_time: '15:00',
        check_out_time: '10:00',
        reservation_status: 'check_in',
        timezone: 'America/Los_Angeles',
        total_guests: 6,
        details: { total_guests: 6, total_adults: 4, total_children: 2, total_pets: 1 }
      });
    }
    if (dayOffset === 7) {
      reservations.push({
        id: resId++,
        address_id: 623559,
        check_in_date: this.formatDate(date),
        check_out_date: this.formatDate(this.addDays(date, 3)),
        check_in_time: '16:00',
        check_out_time: '11:00',
        reservation_status: 'check_in',
        timezone: 'America/Los_Angeles',
        total_guests: 2,
        details: { total_guests: 2, total_adults: 2, total_children: 0, total_pets: 0 }
      });
    }
    return reservations;
  }

  private generateRegularJobs(date: Date, dayOffset: number): any[] {
    const dateStr = this.formatDate(date);
    const dateSlash = this.formatDateSlash(date);
    const jobs: any[] = [];
    let jobId = 3000000 + dayOffset * 100;
    if (dayOffset === 0 || dayOffset === 7) {
      jobs.push({
        id: jobId++,
        date: dateStr,
        address_id: 700001,
        service_name: '2.5 hour cleaning',
        service_category: 'regular_cleaning',
        service_icon_url: 'https://d30thx9uw6scot.cloudfront.net/svgs/regular_cleaning.svg',
        start_time: '10:00',
        timezone: 'America/Los_Angeles',
        is_private: true,
        job_progress: dayOffset < 0 ? 'completed' : 'scheduled',
        is_job_request: false,
        booking: { id: jobId, start_date_no_earlier_than: dateSlash, start_time_no_earlier_than: '10:00am', end_date_no_later_than: dateSlash, end_time_no_later_than: '1:00pm' },
        homekeeper_jobs: [{ id: jobId + 1000, homekeeper: { id: 300001, name: 'Maria Garcia' } }]
      });
    }
    if (dayOffset === 3) {
      jobs.push({
        id: jobId++,
        date: dateStr,
        address_id: 700001,
        service_name: 'Window Cleaning',
        service_category: 'handyman',
        service_icon_url: 'https://d30thx9uw6scot.cloudfront.net/svgs/handyman.svg',
        start_time: '14:00',
        timezone: 'America/Los_Angeles',
        is_private: true,
        job_progress: 'scheduled',
        is_job_request: false,
        booking: { id: jobId, start_date_no_earlier_than: dateSlash, start_time_no_earlier_than: '2:00pm', end_date_no_later_than: dateSlash, end_time_no_later_than: '4:00pm' },
        homekeeper_jobs: [{ id: jobId + 1000, homekeeper: { id: 300002, name: 'John Smith' } }]
      });
    }
    if (dayOffset === -1) {
      jobs.push({
        id: jobId++,
        date: dateStr,
        address_id: 700001,
        service_name: '2.5 hour cleaning',
        service_category: 'regular_cleaning',
        service_icon_url: 'https://d30thx9uw6scot.cloudfront.net/svgs/regular_cleaning.svg',
        start_time: '10:00',
        timezone: 'America/Los_Angeles',
        is_private: true,
        job_progress: 'completed',
        is_job_request: false,
        booking: { id: jobId, start_date_no_earlier_than: dateSlash, start_time_no_earlier_than: '10:00am', end_date_no_later_than: dateSlash, end_time_no_later_than: '1:00pm' },
        homekeeper_jobs: [{ id: jobId + 1000, homekeeper: { id: 300001, name: 'Maria Garcia' } }]
      });
    }
    return jobs;
  }

  private generateCompanyJobs(date: Date, dayOffset: number): any[] {
    const dateStr = this.formatDate(date);
    const dateSlash = this.formatDateSlash(date);
    const jobs: any[] = [];
    let jobId = 4000000 + dayOffset * 100;
    const dayOfWeek = date.getDay();
    if (dayOfWeek >= 1 && dayOfWeek <= 5) {
      jobs.push({
        id: jobId++,
        date: dateStr,
        address_id: 800001,
        service_name: 'Office Cleaning',
        service_category: 'regular_cleaning',
        service_icon_url: 'https://d30thx9uw6scot.cloudfront.net/svgs/regular_cleaning.svg',
        start_time: '18:00',
        timezone: 'America/Los_Angeles',
        is_private: true,
        job_progress: dayOffset < 0 ? 'completed' : 'scheduled',
        is_job_request: false,
        booking: { id: jobId, start_date_no_earlier_than: dateSlash, start_time_no_earlier_than: '6:00pm', end_date_no_later_than: dateSlash, end_time_no_later_than: '9:00pm' },
        homekeeper_jobs: [{ id: jobId + 1000, homekeeper: { id: 400001, name: 'CleanCorp Services' } }]
      });
    }
    if (dayOfWeek === 1) {
      jobs.push({
        id: jobId++,
        date: dateStr,
        address_id: 800001,
        service_name: 'Trash Out',
        service_category: 'trash_service',
        service_icon_url: 'https://d30thx9uw6scot.cloudfront.net/svgs/trash_service.svg',
        start_time: '07:00',
        timezone: 'America/Los_Angeles',
        is_private: true,
        job_progress: dayOffset < 0 ? 'completed' : 'scheduled',
        is_job_request: false,
        booking: { id: jobId, start_date_no_earlier_than: dateSlash, start_time_no_earlier_than: '7:00am', end_date_no_later_than: dateSlash, end_time_no_later_than: '7:30am' },
        homekeeper_jobs: [{ id: jobId + 1000, homekeeper: { id: 400002, name: 'Waste Management Pro' } }]
      });
    }
    if (dayOfWeek === 3) {
      jobs.push({
        id: jobId++,
        date: dateStr,
        address_id: 800002,
        service_name: 'Break Room Deep Clean',
        service_category: 'regular_cleaning',
        service_icon_url: 'https://d30thx9uw6scot.cloudfront.net/svgs/regular_cleaning.svg',
        start_time: '12:00',
        timezone: 'America/Los_Angeles',
        is_private: true,
        job_progress: dayOffset < 0 ? 'completed' : 'scheduled',
        is_job_request: false,
        booking: { id: jobId, start_date_no_earlier_than: dateSlash, start_time_no_earlier_than: '12:00pm', end_date_no_later_than: dateSlash, end_time_no_later_than: '1:00pm' },
        homekeeper_jobs: [{ id: jobId + 1000, homekeeper: { id: 400001, name: 'CleanCorp Services' } }]
      });
    }
    if (dayOfWeek === 5) {
      jobs.push({
        id: jobId++,
        date: dateStr,
        address_id: 800001,
        service_name: 'Conference Room Setup',
        service_category: 'handyman',
        service_icon_url: 'https://d30thx9uw6scot.cloudfront.net/svgs/handyman.svg',
        start_time: '08:00',
        timezone: 'America/Los_Angeles',
        is_private: true,
        job_progress: dayOffset < 0 ? 'completed' : 'scheduled',
        is_job_request: false,
        booking: { id: jobId, start_date_no_earlier_than: dateSlash, start_time_no_earlier_than: '8:00am', end_date_no_later_than: dateSlash, end_time_no_later_than: '9:00am' },
        homekeeper_jobs: [{ id: jobId + 1000, homekeeper: { id: 400003, name: 'Office Setup Pro' } }]
      });
    }
    return jobs;
  }

  getPropertiesMockData(accountType?: AccountType): any[] {
    const type = accountType || this.getAccountType();
    if (type === 'rental') {
      return [
        { id: 623549, address1: 'The Signature at MGM Grand - Tower B', city: 'Las Vegas', add_state: 'NV', zip: '89109', address_name: 'City View Condo', latitude: 36.1067937, longitude: -115.1665441, timezone: 'America/Los_Angeles', integration: { name: 'Guesty', state: 'active' }, address_automatic_booking_setting: { automatic_booking_enabled: true, booking_start_time_no_earlier_than: '11:00', booking_end_time_no_later_than: '16:00' } },
        { id: 623561, address1: '1918 Pintura Circle', city: 'North Las Vegas', add_state: 'NV', zip: '89031', address_name: 'Mountain Retreat', latitude: 36.2512335, longitude: -115.1693707, timezone: 'America/Los_Angeles', integration: { name: 'Guesty', state: 'active' }, address_automatic_booking_setting: { automatic_booking_enabled: true, booking_start_time_no_earlier_than: '11:00', booking_end_time_no_later_than: '16:00' } },
        { id: 623559, address1: '1822 Nuevo Road', city: 'Henderson', add_state: 'NV', zip: '89014', address_name: 'Beach Bungalow', latitude: 36.050533, longitude: -115.067474, timezone: 'America/Los_Angeles', integration: { name: 'Guesty', state: 'active' }, address_automatic_booking_setting: { automatic_booking_enabled: true, booking_start_time_no_earlier_than: '11:00', booking_end_time_no_later_than: '16:00' } },
        { id: 623550, address1: '8157 Creek Water Lane', city: 'Las Vegas', add_state: 'NV', zip: '89123', address_name: 'Cozy Cottage', latitude: 36.0411866, longitude: -115.1420048, timezone: 'America/Los_Angeles', integration: { name: 'Guesty', state: 'active' }, address_automatic_booking_setting: { automatic_booking_enabled: true, booking_start_time_no_earlier_than: '11:00', booking_end_time_no_later_than: '16:00' } },
        { id: 623556, address1: '3149 Belvedere Drive', city: 'Henderson', add_state: 'NV', zip: '89014', address_name: 'Luxury Villa', latitude: 36.063164, longitude: -115.089806, timezone: 'America/Los_Angeles', integration: { name: 'Guesty', state: 'active' }, address_automatic_booking_setting: { automatic_booking_enabled: true, booking_start_time_no_earlier_than: '11:00', booking_end_time_no_later_than: '16:00' } },
        { id: 623552, address1: '4200 South Valley View Boulevard', city: 'Las Vegas', add_state: 'NV', zip: '89103', address_name: 'Desert Oasis', latitude: 36.1126358, longitude: -115.187581, timezone: 'America/Los_Angeles', integration: { name: 'Guesty', state: 'active' }, address_automatic_booking_setting: { automatic_booking_enabled: true, booking_start_time_no_earlier_than: '11:00', booking_end_time_no_later_than: '16:00' } },
        { id: 623547, address1: '120 West Highland Drive', city: 'Henderson', add_state: 'NV', zip: '89015', address_name: 'Secluded Cabin', latitude: 36.0209711, longitude: -114.9885103, timezone: 'America/Los_Angeles', integration: { name: null, state: null }, address_automatic_booking_setting: { automatic_booking_enabled: false } },
        { id: 623563, address1: 'Gabriel Drive', city: 'Las Vegas', add_state: 'NV', zip: '89119', address_name: 'Modern Apartment', latitude: 36.1045715, longitude: -115.1255341, timezone: 'America/Los_Angeles', integration: { name: 'Guesty', state: 'active' }, address_automatic_booking_setting: { automatic_booking_enabled: true, booking_start_time_no_earlier_than: '11:00', booking_end_time_no_later_than: '16:00' } }
      ];
    } else if (type === 'company') {
      return [
        { id: 800001, address1: '100 Main Street', city: 'San Francisco', add_state: 'CA', zip: '94102', address_name: 'Main Office', latitude: 37.7749, longitude: -122.4194, timezone: 'America/Los_Angeles', integration: { name: null, state: null }, address_automatic_booking_setting: { automatic_booking_enabled: false } },
        { id: 800002, address1: '200 Market Street', city: 'San Francisco', add_state: 'CA', zip: '94102', address_name: 'Branch Office', latitude: 37.7751, longitude: -122.4180, timezone: 'America/Los_Angeles', integration: { name: null, state: null }, address_automatic_booking_setting: { automatic_booking_enabled: false } }
      ];
    } else {
      return [
        { id: 700001, address1: '123 Oak Street', city: 'Los Angeles', add_state: 'CA', zip: '90001', address_name: 'My Home', latitude: 34.0522, longitude: -118.2437, timezone: 'America/Los_Angeles', integration: { name: null, state: null }, address_automatic_booking_setting: { automatic_booking_enabled: false } }
      ];
    }
  }

  getMessagesMockData(accountType?: AccountType): any[] {
    const type = accountType || this.getAccountType();
    const today = new Date();
    if (type === 'rental') {
      return this.generateRentalMessages(today);
    } else if (type === 'company') {
      return this.generateCompanyMessages(today);
    } else {
      return this.generateRegularMessages(today);
    }
  }

  private generateRentalMessages(today: Date): any[] {
    const properties = this.getPropertiesMockData('rental');
    const pros = this.getMyProsMockData('rental');
    return [
      this.createMessageObject(1, pros[0], properties[2], "Heads up - I'm running about 20 min behind for the Beach Bungalow turnover. Guest checkout was late. Should still have it ready by 3pm.", 'Homekeeper', true, 'scheduled', this.addHours(today, -2)),
      this.createMessageObject(2, pros[0], properties[0], "Confirming we are on track for this back to back. Next guest checks in at 4pm - can you prioritize the master bedroom and bathrooms first?", 'CustomerMember', true, 'scheduled', this.addHours(today, -4)),
      this.createMessageObject(3, pros[1], properties[1], "Found a stain on the living room carpet at Mountain Retreat - looks like red wine. Going to spot treat it but wanted you to know in case guest mentions anything.", 'Homekeeper', false, 'completed', this.addHours(today, -6)),
      this.createMessageObject(4, pros[2], properties[4], "3 sets of sheets and 6 towels for pickup at Luxury Villa. I bagged them and left by the garage door. Also the duvet cover has a small tear - should I replace it?", 'Homekeeper', false, 'completed', this.addHours(today, -8)),
      this.createMessageObject(5, pros[1], properties[5], "Low on supplies at Desert Oasis: down to last roll of paper towels, 2 toilet paper rolls, no dish soap. Do you want me to grab some or will there be a delivery?", 'Homekeeper', false, 'completed', this.addHours(today, -24)),
      this.createMessageObject(6, pros[0], properties[3], "Guest at Cozy Cottage requested late checkout til 1pm. Can you push your start time back to 1:30? Next check-in is at 5pm so we have some buffer.", 'CustomerMember', true, 'scheduled', this.addHours(today, -26)),
      this.createMessageObject(7, pros[3], properties[6], "Bathroom faucet at Secluded Cabin is dripping pretty badly. Also noticed the AC filter is really dirty - been a while since it was changed?", 'Homekeeper', false, 'completed', this.addHours(today, -48)),
      this.createMessageObject(8, pros[2], properties[7], "Turnover done at Modern Apartment. All beds made, bathrooms clean, kitchen restocked. Left the welcome basket on the counter. Ready for 4pm check-in.", 'Homekeeper', true, 'completed', this.addHours(today, -50)),
      this.createMessageObject(9, pros[1], properties[0], "Hot tub at City View Condo looks cloudy. I tested the water and pH is way off. Might need the pool guy before guests tonight.", 'Homekeeper', false, 'completed', this.addHours(today, -72)),
      this.createMessageObject(10, pros[0], properties[2], "Can't get into Beach Bungalow - the lockbox code isn't working. Guest might have changed it? Tried 4521 like usual.", 'Homekeeper', false, 'scheduled', this.addHours(today, -74)),
      this.createMessageObject(11, pros[2], properties[4], "Broken lamp in the master bedroom at Luxury Villa and a cracked tile in the shower. Took photos for you. Previous guests maybe?", 'Homekeeper', false, 'completed', this.addHours(today, -96)),
      this.createMessageObject(12, pros[1], properties[1], "Any chance I can swap my Tuesday and Wednesday turnovers this week? Have a doctor appt Tuesday afternoon.", 'Homekeeper', false, 'scheduled', this.addHours(today, -100))
    ];
  }

  private generateRegularMessages(today: Date): any[] {
    const properties = this.getPropertiesMockData('regular');
    const pros = this.getMyProsMockData('regular');
    return [
      this.createMessageObject(1, pros[0], properties[0], "Hi! Just confirming our cleaning tomorrow at 10am. Any specific areas you'd like me to focus on this time?", 'Homekeeper', true, 'scheduled', this.addHours(today, -2)),
      this.createMessageObject(2, pros[0], properties[0], "Hey Maria, something came up. Can we move the cleaning to Thursday instead? Same time works for me.", 'CustomerMember', true, 'scheduled', this.addHours(today, -4)),
      this.createMessageObject(3, pros[1], properties[0], "I noticed a small leak under your kitchen sink while cleaning today. Might want to have a plumber look at it before it gets worse.", 'Homekeeper', false, 'completed', this.addHours(today, -24)),
      this.createMessageObject(4, pros[0], properties[0], "Running low on cleaning supplies at your place. Down to the last of the all-purpose cleaner and almost out of trash bags. Should I pick some up?", 'Homekeeper', false, 'completed', this.addHours(today, -48)),
      this.createMessageObject(5, pros[0], properties[0], "Hey, just wanted to let you know our dog will be home during the cleaning. He's friendly but might follow you around. His name is Max.", 'CustomerMember', true, 'scheduled', this.addHours(today, -50)),
      this.createMessageObject(6, pros[1], properties[0], "All done! Deep cleaned the bathrooms like you asked and did extra work on the kitchen floors. See you next week!", 'Homekeeper', true, 'completed', this.addHours(today, -72)),
      this.createMessageObject(7, pros[0], properties[0], "I'm here but the garage code isn't working. Is it still 1234 or did it change?", 'Homekeeper', false, 'scheduled', this.addHours(today, -74)),
      this.createMessageObject(8, pros[0], properties[0], "We have guests coming this weekend - could you do a more thorough cleaning of the guest bedroom and bathroom on Thursday?", 'CustomerMember', true, 'scheduled', this.addHours(today, -96)),
      this.createMessageObject(9, pros[1], properties[0], "Your vacuum cleaner is making a weird noise and not picking up well. Might need a new filter or bag. Want me to check?", 'Homekeeper', false, 'completed', this.addHours(today, -100)),
      this.createMessageObject(10, pros[0], properties[0], "I'm so sorry but I have a family emergency and can't make it tomorrow. Could we reschedule to Friday?", 'Homekeeper', false, 'scheduled', this.addHours(today, -120)),
      this.createMessageObject(11, pros[1], properties[0], "Found a ring under the couch cushion while cleaning today. Left it on your kitchen counter by the fruit bowl.", 'Homekeeper', false, 'completed', this.addHours(today, -144)),
      this.createMessageObject(12, pros[0], properties[0], "This works great! Can we set up a regular weekly cleaning every Thursday at 10am going forward?", 'CustomerMember', true, 'completed', this.addHours(today, -168))
    ];
  }

  private generateCompanyMessages(today: Date): any[] {
    const properties = this.getPropertiesMockData('company');
    const pros = this.getMyProsMockData('company');
    return [
      this.createMessageObject(1, pros[0], properties[0], "Office cleaning complete at Main Office. Vacuumed all floors, emptied trash, and sanitized all common areas. Locked up at 9pm.", 'Homekeeper', true, 'completed', this.addHours(today, -2)),
      this.createMessageObject(2, pros[0], properties[1], "Break room fridge at Branch Office needs attention - there's some expired food that's been there a while. Want us to clear it out during tonight's cleaning?", 'Homekeeper', false, 'scheduled', this.addHours(today, -4)),
      this.createMessageObject(3, pros[0], properties[0], "FYI the conference room on 3rd floor had a big mess after what looks like a catered lunch. Spent extra time on it but all clean now.", 'Homekeeper', false, 'completed', this.addHours(today, -24)),
      this.createMessageObject(4, pros[1], properties[0], "Restocking supplies are running low: hand sanitizer, paper towels in restrooms, and the soap dispensers need refills. Should I add to the order?", 'Homekeeper', false, 'completed', this.addHours(today, -48)),
      this.createMessageObject(5, pros[0], properties[1], "Someone left the AC running in the break room overnight again. Just wanted to flag it - building was pretty warm when I arrived.", 'Homekeeper', false, 'completed', this.addHours(today, -50)),
      this.createMessageObject(6, pros[0], properties[0], "We have a board meeting tomorrow - can the team do an extra thorough clean of the executive conference room tonight?", 'CustomerMember', true, 'scheduled', this.addHours(today, -72)),
      this.createMessageObject(7, pros[1], properties[0], "Window cleaning crew is scheduled for Saturday morning. I'll coordinate access - is 8am too early?", 'Homekeeper', false, 'scheduled', this.addHours(today, -74)),
      this.createMessageObject(8, pros[0], properties[1], "Noticed some carpet stains near the copy room. Might need a professional carpet cleaning soon - want me to get a quote?", 'Homekeeper', false, 'completed', this.addHours(today, -96)),
      this.createMessageObject(9, pros[0], properties[0], "All done for the night. Restrooms restocked, floors mopped, all trash removed. See you Wednesday!", 'Homekeeper', true, 'completed', this.addHours(today, -100)),
      this.createMessageObject(10, pros[1], properties[0], "The cleaning closet door lock is sticking again. Had trouble getting supplies out tonight. Might need maintenance to look at it.", 'Homekeeper', false, 'completed', this.addHours(today, -120))
    ];
  }

  private createMessageObject(index: number, pro: any, property: any, text: string, fromType: 'Homekeeper' | 'CustomerMember', isRead: boolean, jobState: string, sentAt: Date): any {
    const jobId = 2000000 + index * 100;
    const homekeeperId = pro.id;
    const teamId = pro.main_team_id;
    const fromMember = fromType === 'Homekeeper'
      ? { id: homekeeperId, type: 'Homekeeper', first_name: pro.first_name, last_name: pro.last_name }
      : { id: 541222, type: 'CustomerMember', first_name: 'Riley', last_name: 'Frederickson' };
    return {
      type: 'homekeeper_job',
      chat_rooms: [{
        key: `homekeeper_job_id-${jobId + 1000}`,
        type: 'homekeeper_job',
        data: {
          job_id: jobId,
          team_id: teamId,
          homekeeper_id: homekeeperId,
          homekeeper_job_id: jobId + 1000,
          customer_account_id: 543457
        },
        proxy_type: null,
        expires_at: null,
        chat_phone_number: null,
        count_chat_room_messages: 1,
        last_chat_room_message: {
          id: 700000 + index,
          type: 'text',
          content: { text, files: [] },
          sent_at: sentAt.toISOString(),
          is_read: isRead,
          is_internal: false,
          from_member: fromMember
        },
        all_chat_room_members: [
          { id: homekeeperId, type: 'Homekeeper', first_name: pro.first_name, last_name: pro.last_name },
          { id: 541222, type: 'CustomerMember', first_name: 'Riley', last_name: 'Frederickson' }
        ]
      }],
      job: {
        id: jobId,
        state: jobState,
        address_id: property.id,
        is_private: true,
        timezone_name: property.timezone || 'America/Los_Angeles',
        start_datetime: sentAt.toISOString()
      }
    };
  }

  private addHours(date: Date, hours: number): Date {
    const result = new Date(date);
    result.setHours(result.getHours() + hours);
    return result;
  }

  getMyProsMockData(accountType?: AccountType): any[] {
    const type = accountType || this.getAccountType();
    if (type === 'rental') {
      const properties = this.getPropertiesMockData('rental');
      const today = new Date();
      const at = (days: number, hour: number, min = 0) => {
        const d = this.addDays(today, days);
        d.setHours(hour, min, 0, 0);
        return d.toISOString();
      };
      const pastJob = (days: number, hour: number) => ({ date: at(-days, hour), start_time: at(-days, hour) });
      const futureJob = (days: number, hour: number) => ({ date: at(days, hour), start_time: at(days, hour) });
      const ahp = (propertyIndex: number, serviceName: string, serviceIcon?: string) => ({
        address_id: properties[propertyIndex]?.id,
        address_name: properties[propertyIndex]?.address_name,
        service_type_id: 1,
        service_name: serviceName,
        service_icon: serviceIcon || null,
      });
      return [
        { id: 221879, main_team_id: 218880, name: 'Aurelia Ili', first_name: 'Aurelia', last_name: 'Ili', state: 'active', is_private: true, team_name: "Aurelia's Turnover Team",
          address_homekeeper_priorities: [ahp(0, 'Regular Cleaning', 'assets/img/mop.png'), ahp(1, 'Regular Cleaning', 'assets/img/mop.png'), ahp(2, 'Deep Cleaning', 'assets/img/mop.png')],
          last_job: pastJob(2, 10), next_job: futureJob(3, 11) },
        { id: 230477, main_team_id: 227475, name: 'Harold Lesure', first_name: 'Harold', last_name: 'Lesure', state: 'active', is_private: true, team_name: 'Quick Turn Cleaners',
          address_homekeeper_priorities: [ahp(3, 'Regular Cleaning', 'assets/img/mop.png'), ahp(4, 'Deep Cleaning', 'assets/img/mop.png')],
          last_job: pastJob(1, 9), next_job: futureJob(2, 13) },
        { id: 231013, main_team_id: 228010, name: 'Maria Mendoza', first_name: 'Maria', last_name: 'Mendoza', state: 'active', is_private: true, team_name: "Maria's Cleaning Co",
          address_homekeeper_priorities: [ahp(5, 'Regular Cleaning', 'assets/img/mop.png'), ahp(6, 'Linen Change')],
          last_job: pastJob(4, 10), next_job: futureJob(1, 12) },
        { id: 230481, main_team_id: 227479, name: 'Tiffany Humphrey', first_name: 'Tiffany', last_name: 'Humphrey', state: 'active', is_private: true, team_name: 'Pool & Spa Pros',
          address_homekeeper_priorities: [ahp(0, 'Pool Cleaning'), ahp(4, 'Pool Cleaning'), ahp(5, 'Hot Tub Service')],
          last_job: pastJob(3, 14), next_job: futureJob(4, 14) },
        { id: 232001, main_team_id: 229001, name: 'Carlos Rivera', first_name: 'Carlos', last_name: 'Rivera', state: 'active', is_private: true, team_name: 'Rivera Lawn Care',
          address_homekeeper_priorities: [ahp(1, 'Lawn Care'), ahp(2, 'Lawn Care'), ahp(7, 'Lawn Care')],
          last_job: pastJob(5, 8), next_job: futureJob(2, 8) },
        { id: 232002, main_team_id: 229002, name: 'Brittany Chen', first_name: 'Brittany', last_name: 'Chen', state: 'active', is_private: true, team_name: 'Sparkle Window Pros',
          address_homekeeper_priorities: [ahp(0, 'Window Cleaning'), ahp(3, 'Window Cleaning')],
          last_job: pastJob(8, 10), next_job: futureJob(7, 10) },
        { id: 232003, main_team_id: 229003, name: 'Devon Walker', first_name: 'Devon', last_name: 'Walker', state: 'active', is_private: true, team_name: 'Walker Handyman',
          address_homekeeper_priorities: [ahp(2, 'Handyman'), ahp(6, 'Handyman'), ahp(7, 'Handyman')],
          last_job: pastJob(6, 9), next_job: futureJob(5, 9) },
        { id: 232004, main_team_id: 229004, name: 'Sofia Ramirez', first_name: 'Sofia', last_name: 'Ramirez', state: 'active', is_private: true, team_name: 'Sofia Linens',
          address_homekeeper_priorities: [ahp(0, 'Linen Change'), ahp(1, 'Linen Change'), ahp(3, 'Linen Change'), ahp(4, 'Linen Change')],
          last_job: pastJob(1, 11), next_job: futureJob(1, 11) },
        { id: 232005, main_team_id: 229005, name: 'Jordan Patel', first_name: 'Jordan', last_name: 'Patel', state: 'active', is_private: true, team_name: 'Patel Pest Control',
          address_homekeeper_priorities: [ahp(2, 'Pest Control'), ahp(5, 'Pest Control')],
          last_job: pastJob(15, 13), next_job: futureJob(15, 13) },
        { id: 232006, main_team_id: 229006, name: 'Olivia Brooks', first_name: 'Olivia', last_name: 'Brooks', state: 'active', is_private: true, team_name: 'Brooks Carpet Co',
          address_homekeeper_priorities: [ahp(1, 'Carpet Cleaning'), ahp(4, 'Carpet Cleaning')],
          last_job: pastJob(20, 10), next_job: futureJob(40, 10) },
        { id: 232007, main_team_id: 229007, name: 'Ethan Nguyen', first_name: 'Ethan', last_name: 'Nguyen', state: 'active', is_private: true, team_name: 'Nguyen Plumbing',
          address_homekeeper_priorities: [ahp(0, 'Plumbing'), ahp(3, 'Plumbing'), ahp(6, 'Plumbing')],
          last_job: pastJob(10, 8), next_job: null },
        { id: 232008, main_team_id: 229008, name: 'Priya Singh', first_name: 'Priya', last_name: 'Singh', state: 'active', is_private: true, team_name: 'Singh Electrical',
          address_homekeeper_priorities: [ahp(2, 'Electrical'), ahp(5, 'Electrical')],
          last_job: pastJob(12, 9), next_job: futureJob(20, 9) },
        { id: 232009, main_team_id: 229009, name: 'Marcus Johnson', first_name: 'Marcus', last_name: 'Johnson', state: 'active', is_private: true, team_name: 'Johnson HVAC',
          address_homekeeper_priorities: [ahp(0, 'HVAC'), ahp(1, 'HVAC'), ahp(4, 'HVAC'), ahp(7, 'HVAC')],
          last_job: pastJob(30, 11), next_job: futureJob(60, 11) },
        { id: 232010, main_team_id: 229010, name: 'Isabella Torres', first_name: 'Isabella', last_name: 'Torres', state: 'active', is_private: true, team_name: 'Torres Painting',
          address_homekeeper_priorities: [ahp(6, 'Painting'), ahp(7, 'Painting')],
          last_job: pastJob(45, 8), next_job: null },
        { id: 232011, main_team_id: 229011, name: 'Liam Anderson', first_name: 'Liam', last_name: 'Anderson', state: 'active', is_private: true, team_name: 'Anderson Locksmith',
          address_homekeeper_priorities: [ahp(0, 'Locksmith'), ahp(2, 'Locksmith'), ahp(5, 'Locksmith')],
          last_job: pastJob(7, 15), next_job: null },
        { id: 232012, main_team_id: 229012, name: 'Emma Robinson', first_name: 'Emma', last_name: 'Robinson', state: 'active', is_private: true, team_name: 'Robinson Laundry',
          address_homekeeper_priorities: [ahp(1, 'Laundry', 'assets/img/mop.png'), ahp(3, 'Laundry', 'assets/img/mop.png'), ahp(6, 'Laundry', 'assets/img/mop.png')],
          last_job: pastJob(2, 9), next_job: futureJob(2, 9) },
        { id: 232013, main_team_id: 229013, name: 'Noah Williams', first_name: 'Noah', last_name: 'Williams', state: 'active', is_private: true, team_name: 'Williams Pressure Wash',
          address_homekeeper_priorities: [ahp(4, 'Pressure Washing'), ahp(7, 'Pressure Washing')],
          last_job: pastJob(35, 8), next_job: futureJob(50, 8) },
        { id: 232014, main_team_id: 229014, name: 'Ava Martinez', first_name: 'Ava', last_name: 'Martinez', state: 'active', is_private: true, team_name: "Ava's Deep Clean",
          address_homekeeper_priorities: [ahp(0, 'Deep Cleaning', 'assets/img/mop.png'), ahp(2, 'Deep Cleaning', 'assets/img/mop.png'), ahp(5, 'Deep Cleaning', 'assets/img/mop.png')],
          last_job: pastJob(9, 10), next_job: futureJob(8, 10) },
        { id: 232015, main_team_id: 229015, name: 'Lucas Gomez', first_name: 'Lucas', last_name: 'Gomez', state: 'active', is_private: true, team_name: 'Gomez Garage Doors',
          address_homekeeper_priorities: [ahp(1, 'Garage Door'), ahp(4, 'Garage Door')],
          last_job: pastJob(60, 11), next_job: null },
        { id: 232016, main_team_id: 229016, name: 'Mia Thompson', first_name: 'Mia', last_name: 'Thompson', state: 'active', is_private: true, team_name: 'Thompson Trash Removal',
          address_homekeeper_priorities: [ahp(0, 'Trash Service'), ahp(1, 'Trash Service'), ahp(2, 'Trash Service'), ahp(3, 'Trash Service')],
          last_job: pastJob(1, 7), next_job: futureJob(6, 7) },
        { id: 232017, main_team_id: 229017, name: 'Henry Foster', first_name: 'Henry', last_name: 'Foster', state: 'active', is_private: true, team_name: 'Foster Restocking',
          address_homekeeper_priorities: [ahp(4, 'Restocking'), ahp(5, 'Restocking'), ahp(6, 'Restocking')],
          last_job: pastJob(4, 9), next_job: futureJob(3, 9) },
        { id: 232018, main_team_id: 229018, name: 'Charlotte Kim', first_name: 'Charlotte', last_name: 'Kim', state: 'active', is_private: true, team_name: 'Kim Inspection',
          address_homekeeper_priorities: [ahp(0, 'Property Inspection'), ahp(7, 'Property Inspection')],
          last_job: pastJob(25, 13), next_job: futureJob(35, 13) },
        { id: 232019, main_team_id: 229019, name: 'Benjamin Hayes', first_name: 'Benjamin', last_name: 'Hayes', state: 'active', is_private: true, team_name: 'Hayes Appliance Repair',
          address_homekeeper_priorities: [ahp(2, 'Appliance Repair'), ahp(3, 'Appliance Repair')],
          last_job: pastJob(18, 14), next_job: null },
        { id: 232020, main_team_id: 229020, name: 'Grace Reyes', first_name: 'Grace', last_name: 'Reyes', state: 'active', is_private: true, team_name: 'Reyes Welcome Setup',
          address_homekeeper_priorities: [ahp(0, 'Welcome Setup'), ahp(1, 'Welcome Setup'), ahp(2, 'Welcome Setup'), ahp(3, 'Welcome Setup'), ahp(4, 'Welcome Setup'), ahp(5, 'Welcome Setup')],
          last_job: pastJob(1, 15), next_job: futureJob(1, 15) }
      ];
    } else if (type === 'company') {
      return [
        { id: 400001, main_team_id: 400100, name: 'CleanCorp Services', first_name: 'CleanCorp', last_name: 'Services', state: 'active', is_private: true, team_name: 'CleanCorp Commercial', address_homekeeper_priorities: [], next_job: null, last_job: null },
        { id: 400002, main_team_id: 400101, name: 'Facility Pro', first_name: 'Facility', last_name: 'Pro', state: 'active', is_private: true, team_name: 'Facility Maintenance', address_homekeeper_priorities: [], next_job: null, last_job: null }
      ];
    } else {
      return [
        { id: 300001, main_team_id: 300100, name: 'Maria Garcia', first_name: 'Maria', last_name: 'Garcia', state: 'active', is_private: true, team_name: "Maria's Home Cleaning", address_homekeeper_priorities: [], next_job: null, last_job: null },
        { id: 300002, main_team_id: 300101, name: 'John Smith', first_name: 'John', last_name: 'Smith', state: 'active', is_private: true, team_name: "John's Handyman Services", address_homekeeper_priorities: [], next_job: null, last_job: null }
      ];
    }
  }

  getGuestTenantsMockData(accountType?: AccountType): any[] {
    const type = accountType || this.getAccountType();
    const today = new Date();
    const properties = this.getPropertiesMockData(type);
    const past = (days: number) => this.addDays(today, -days).toISOString();
    if (type === 'rental') {
      return [
        { name: 'Sarah Mitchell', email: 'sarah.mitchell@example.com', phone: '+14155550101', properties: [properties[0]?.address_name], stays: 3, source: 'Airbnb', last_stay: past(5) },
        { name: 'James Anderson', email: 'james.a@example.com', phone: '+14155550102', properties: [properties[1]?.address_name, properties[2]?.address_name], stays: 5, source: 'Vrbo', last_stay: past(12) },
        { name: 'Priya Sharma', email: 'priya.sharma@example.com', phone: '+14155550103', properties: [properties[3]?.address_name], stays: 1, source: 'Booking.com', last_stay: past(18) },
        { name: 'Michael Chen', email: 'm.chen@example.com', phone: '+14155550104', properties: [properties[4]?.address_name], stays: 2, source: 'Direct', last_stay: past(22) },
        { name: 'Emily Rodriguez', email: 'emily.r@example.com', phone: '+14155550105', properties: [properties[5]?.address_name, properties[0]?.address_name], stays: 4, source: 'Airbnb', last_stay: past(28) },
        { name: 'David Park', email: 'david.park@example.com', phone: '+14155550106', properties: [properties[6]?.address_name], stays: 1, source: 'Vrbo', last_stay: past(35) },
        { name: 'Olivia Bennett', email: 'olivia.b@example.com', phone: '+14155550107', properties: [properties[7]?.address_name], stays: 6, source: 'Airbnb', last_stay: past(42) },
        { name: 'Noah Wilson', email: 'noah.w@example.com', phone: '+14155550108', properties: [properties[2]?.address_name], stays: 2, source: 'Booking.com', last_stay: past(48) },
        { name: 'Sophia Garcia', email: 'sophia.g@example.com', phone: '+14155550109', properties: [properties[1]?.address_name], stays: 3, source: 'Direct', last_stay: past(55) },
        { name: 'Liam Thompson', email: 'liam.t@example.com', phone: '+14155550110', properties: [properties[3]?.address_name, properties[4]?.address_name], stays: 7, source: 'Airbnb', last_stay: past(60) },
        { name: 'Isabella Martinez', email: 'isabella.m@example.com', phone: '+14155550111', properties: [properties[0]?.address_name], stays: 1, source: 'Vrbo', last_stay: past(70) },
        { name: 'Ethan Brooks', email: 'ethan.b@example.com', phone: '+14155550112', properties: [properties[5]?.address_name], stays: 2, source: 'Airbnb', last_stay: past(80) },
        { name: 'Mia Reynolds', email: 'mia.r@example.com', phone: '+14155550113', properties: [properties[6]?.address_name, properties[7]?.address_name], stays: 4, source: 'Booking.com', last_stay: past(90) },
        { name: 'Lucas Hayes', email: 'lucas.h@example.com', phone: '+14155550114', properties: [properties[2]?.address_name], stays: 1, source: 'Direct', last_stay: past(100) },
        { name: 'Ava Patel', email: 'ava.p@example.com', phone: '+14155550115', properties: [properties[4]?.address_name], stays: 3, source: 'Airbnb', last_stay: past(110) },
        { name: 'Henry Walker', email: 'henry.w@example.com', phone: '+14155550116', properties: [properties[1]?.address_name], stays: 2, source: 'Vrbo', last_stay: past(120) },
        // By-the-room tenants for the split-rental demo units (see rental-units.service.ts)
        { name: 'Marcus Whitfield', email: 'marcus.whitfield@example.com', phone: '+18135550117', properties: ['Palmetto House'], stays: 1, source: 'Lease', last_stay: past(1) },
        { name: 'Keisha Boone', email: 'keisha.boone@example.com', phone: '+18135550118', properties: ['Palmetto House'], stays: 1, source: 'Lease', last_stay: past(1) },
        { name: 'Felix Ostrander', email: 'felix.ostrander@example.com', phone: '+18135550119', properties: ['Palmetto House'], stays: 1, source: 'Lease', last_stay: past(1) },
        { name: 'Renata Cho', email: 'renata.cho@example.com', phone: '+16145550120', properties: ['Ferncreek Rooming House'], stays: 1, source: 'Lease', last_stay: past(1) },
        { name: 'Dwayne Pruitt', email: 'dwayne.pruitt@example.com', phone: '+16145550121', properties: ['Ferncreek Rooming House'], stays: 1, source: 'Lease', last_stay: past(1) }
      ];
    } else if (type === 'company') {
      return [
        { name: 'Acme Corp', email: 'contact@acme.example.com', phone: '+14155550201', properties: [properties[0]?.address_name], stays: 12, source: 'Direct', last_stay: past(2) },
        { name: 'Globex Industries', email: 'admin@globex.example.com', phone: '+14155550202', properties: [properties[1]?.address_name], stays: 8, source: 'Direct', last_stay: past(7) },
        { name: 'Initech LLC', email: 'office@initech.example.com', phone: '+14155550203', properties: [properties[0]?.address_name, properties[1]?.address_name], stays: 4, source: 'Direct', last_stay: past(15) }
      ];
    } else {
      return [
        { name: 'Family Member', email: 'family@example.com', phone: '+14155550301', properties: [properties[0]?.address_name], stays: 10, source: 'Direct', last_stay: past(5) },
        { name: 'House Guest', email: 'guest@example.com', phone: '+14155550302', properties: [properties[0]?.address_name], stays: 2, source: 'Direct', last_stay: past(20) }
      ];
    }
  }

  getBillsMockData(accountType?: AccountType): any[] {
    const type = accountType || this.getAccountType();
    const today = new Date();
    const properties = this.getPropertiesMockData(type);
    const pros = this.getMyProsMockData(type);
    if (type === 'rental') {
      return [
        this.createBillObject(1, pros[0], properties[0], 8500, '2.5 hour cleaning', 'Regular Cleaning', this.addDays(today, -1)),
        this.createBillObject(2, pros[1], properties[1], 8500, '2.5 hour cleaning', 'Regular Cleaning', this.addDays(today, -2)),
        this.createBillObject(3, pros[2], properties[2], 6000, '2.5 hour cleaning', 'Regular Cleaning', this.addDays(today, -3)),
        this.createBillObject(4, pros[0], properties[3], 10500, '4 hour cleaning', 'Deep Cleaning', this.addDays(today, -4)),
        this.createBillObject(5, pros[3], properties[4], 6500, 'Pool Cleaning', 'Pool Cleaning', this.addDays(today, -5)),
        this.createBillObject(6, pros[1], properties[5], 8500, '2.5 hour cleaning', 'Regular Cleaning', this.addDays(today, -6)),
        this.createBillObject(7, pros[2], properties[0], 4500, 'Laundry Pickup', 'Laundry', this.addDays(today, -7)),
        this.createBillObject(8, pros[0], properties[1], 8500, '2.5 hour cleaning', 'Regular Cleaning', this.addDays(today, -8)),
        this.createBillObject(9, pros[3], properties[2], 7500, '2.5 hour cleaning', 'Regular Cleaning', this.addDays(today, -9)),
        this.createBillObject(10, pros[1], properties[3], 12000, 'Deep Cleaning', 'Deep Cleaning', this.addDays(today, -10)),
        this.createBillObject(11, pros[0], properties[4], 5500, 'Linen Service', 'Linen Change', this.addDays(today, -11)),
        this.createBillObject(12, pros[2], properties[5], 8500, '2.5 hour cleaning', 'Regular Cleaning', this.addDays(today, -12)),
        this.createBillObject(13, pros[3], properties[0], 9500, '3 hour cleaning', 'Regular Cleaning', this.addDays(today, -13)),
        this.createBillObject(14, pros[0], properties[1], 15000, 'Move-out Cleaning', 'Deep Cleaning', this.addDays(today, -14)),
        this.createBillObject(15, pros[1], properties[2], 6000, '2.5 hour cleaning', 'Regular Cleaning', this.addDays(today, -15)),
        this.createBillObject(16, pros[2], properties[3], 7000, 'Carpet Cleaning', 'Carpet Cleaning', this.addDays(today, -16)),
        this.createBillObject(17, pros[0], properties[4], 8500, '2.5 hour cleaning', 'Regular Cleaning', this.addDays(today, -17)),
        this.createBillObject(18, pros[3], properties[5], 4000, 'Trash Removal', 'Trash Service', this.addDays(today, -18)),
        this.createBillObject(19, pros[1], properties[0], 8500, '2.5 hour cleaning', 'Regular Cleaning', this.addDays(today, -19)),
        this.createBillObject(20, pros[2], properties[1], 11000, 'Window Cleaning', 'Window Cleaning', this.addDays(today, -20)),
        this.createBillObject(21, pros[0], properties[2], 6000, '2.5 hour cleaning', 'Regular Cleaning', this.addDays(today, -21)),
        this.createBillObject(22, pros[3], properties[3], 8500, '2.5 hour cleaning', 'Regular Cleaning', this.addDays(today, -22)),
        this.createBillObject(23, pros[1], properties[4], 13500, '4 hour cleaning', 'Deep Cleaning', this.addDays(today, -23)),
        this.createBillObject(24, pros[0], properties[5], 6500, 'Pool Cleaning', 'Pool Cleaning', this.addDays(today, -24))
      ];
    } else if (type === 'company') {
      return [
        this.createBillObject(1, pros[0], properties[0], 25000, 'Office Cleaning', 'Commercial Cleaning', this.addDays(today, -1)),
        this.createBillObject(2, pros[0], properties[1], 18000, 'Office Cleaning', 'Commercial Cleaning', this.addDays(today, -2)),
        this.createBillObject(3, pros[1], properties[0], 8000, 'Restroom Deep Clean', 'Deep Cleaning', this.addDays(today, -5)),
        this.createBillObject(4, pros[0], properties[0], 25000, 'Office Cleaning', 'Commercial Cleaning', this.addDays(today, -8)),
        this.createBillObject(5, pros[1], properties[1], 12000, 'Window Cleaning', 'Window Cleaning', this.addDays(today, -10))
      ];
    } else {
      return [
        this.createBillObject(1, pros[0], properties[0], 8500, '2.5 hour cleaning', 'Regular Cleaning', this.addDays(today, -7)),
        this.createBillObject(2, pros[0], properties[0], 8500, '2.5 hour cleaning', 'Regular Cleaning', this.addDays(today, -14)),
        this.createBillObject(3, pros[1], properties[0], 15000, 'Window Cleaning', 'Window Cleaning', this.addDays(today, -21)),
        this.createBillObject(4, pros[0], properties[0], 8500, '2.5 hour cleaning', 'Regular Cleaning', this.addDays(today, -28)),
        this.createBillObject(5, pros[0], properties[0], 12000, 'Deep Cleaning', 'Deep Cleaning', this.addDays(today, -35))
      ];
    }
  }

  private createBillObject(index: number, pro: any, property: any, amount: number, serviceName: string, serviceType: string, date: Date): any {
    const jobId = 2000000 + index * 100;
    return {
      id: 66000 + index,
      uuid: `00-${Math.random().toString(36).substr(2, 8)}`,
      state: 'active',
      note: null,
      thumbnail_url: 'https://samantha-file-uploads.s3.us-west-2.amazonaws.com/production/invoices/placeholder/thumbnail.png',
      team_id: pro.main_team_id,
      amount,
      full_paid: false,
      amount_due: amount,
      created_at: date.toISOString(),
      homekeeper_data: {
        id: pro.id,
        name: pro.name,
        email: `${pro.first_name.toLowerCase()}@example.com`,
        phone: '+15551234567',
        first_name: pro.first_name
      },
      items: [{
        id: 66000 + index,
        item_type: 'Job',
        item_id: jobId,
        service_details: {
          name: serviceName,
          type: serviceType,
          date: date.toISOString(),
          duration_in_minutes: 150,
          start_time: '11:00',
          address: {
            id: property.id,
            address_name: property.address_name,
            address: property.address1,
            unit: null,
            zip: property.zip,
            city: property.city,
            state: property.add_state
          }
        }
      }]
    };
  }

  getToDosMockData(accountType?: AccountType): any[] {
    const type = accountType || this.getAccountType();
    const properties = this.getPropertiesMockData(type);
    if (type === 'rental') {
      return [
        { id: 353384, title: 'Turnover Checklist', state: 'active', is_customer_list: true, is_template: true, address_ids: [properties[0]?.id, properties[1]?.id], default_list_settings: [], block_complete_on_missing_fields: false, before_after_photos_state: 'active', address_names: [properties[0]?.address_name, properties[1]?.address_name] },
        { id: 353385, title: 'Guest Ready Inspection', state: 'active', is_customer_list: true, is_template: true, address_ids: [properties[0]?.id], default_list_settings: [], block_complete_on_missing_fields: false, before_after_photos_state: 'active', address_name: properties[0]?.address_name },
        { id: 353386, title: 'Laundry Checklist', state: 'active', is_customer_list: true, is_template: true, address_ids: [properties[2]?.id], default_list_settings: [], block_complete_on_missing_fields: false, before_after_photos_state: 'inactive', address_name: properties[2]?.address_name },
        { id: 353387, title: 'Deep Clean Checklist', state: 'active', is_customer_list: true, is_template: true, address_ids: [properties[1]?.id], default_list_settings: [], block_complete_on_missing_fields: false, before_after_photos_state: 'active', address_name: properties[1]?.address_name },
        { id: 353388, title: 'Pool/Hot Tub Maintenance', state: 'active', is_customer_list: true, is_template: true, address_ids: [properties[0]?.id, properties[4]?.id], default_list_settings: [], block_complete_on_missing_fields: false, before_after_photos_state: 'inactive', address_names: [properties[0]?.address_name, properties[4]?.address_name] },
        { id: 353389, title: 'Restocking Inventory', state: 'active', is_customer_list: true, is_template: true, address_ids: properties.map(p => p?.id), default_list_settings: [], block_complete_on_missing_fields: false, before_after_photos_state: 'inactive', address_names: properties.map(p => p?.address_name) }
      ];
    } else if (type === 'company') {
      return [
        { id: 353390, title: 'Nightly Office Cleaning', state: 'active', is_customer_list: true, is_template: true, address_ids: [properties[0]?.id, properties[1]?.id], default_list_settings: [], block_complete_on_missing_fields: false, before_after_photos_state: 'inactive', address_names: [properties[0]?.address_name, properties[1]?.address_name] },
        { id: 353391, title: 'Restroom Deep Clean', state: 'active', is_customer_list: true, is_template: true, address_ids: [properties[0]?.id], default_list_settings: [], block_complete_on_missing_fields: false, before_after_photos_state: 'active', address_name: properties[0]?.address_name },
        { id: 353392, title: 'Break Room Cleaning', state: 'active', is_customer_list: true, is_template: true, address_ids: [properties[0]?.id, properties[1]?.id], default_list_settings: [], block_complete_on_missing_fields: false, before_after_photos_state: 'inactive', address_names: [properties[0]?.address_name, properties[1]?.address_name] },
        { id: 353393, title: 'Monthly Supply Inventory', state: 'active', is_customer_list: true, is_template: true, address_ids: properties.map(p => p?.id), default_list_settings: [], block_complete_on_missing_fields: false, before_after_photos_state: 'inactive', address_names: properties.map(p => p?.address_name) },
        { id: 353394, title: 'Conference Room Setup', state: 'active', is_customer_list: true, is_template: true, address_ids: [properties[0]?.id], default_list_settings: [], block_complete_on_missing_fields: false, before_after_photos_state: 'inactive', address_name: properties[0]?.address_name }
      ];
    } else {
      return [
        { id: 353395, title: 'Weekly Cleaning Checklist', state: 'active', is_customer_list: true, is_template: true, address_ids: [properties[0]?.id], default_list_settings: [], block_complete_on_missing_fields: false, before_after_photos_state: 'inactive', address_name: properties[0]?.address_name },
        { id: 353396, title: 'Deep Cleaning Checklist', state: 'active', is_customer_list: true, is_template: true, address_ids: [properties[0]?.id], default_list_settings: [], block_complete_on_missing_fields: false, before_after_photos_state: 'active', address_name: properties[0]?.address_name },
        { id: 353397, title: 'Kitchen Deep Clean', state: 'active', is_customer_list: true, is_template: true, address_ids: [properties[0]?.id], default_list_settings: [], block_complete_on_missing_fields: false, before_after_photos_state: 'active', address_name: properties[0]?.address_name },
        { id: 353398, title: 'Bathroom Checklist', state: 'active', is_customer_list: true, is_template: true, address_ids: [properties[0]?.id], default_list_settings: [], block_complete_on_missing_fields: false, before_after_photos_state: 'inactive', address_name: properties[0]?.address_name },
        { id: 353399, title: 'Seasonal Maintenance', state: 'active', is_customer_list: true, is_template: true, address_ids: [properties[0]?.id], default_list_settings: [], block_complete_on_missing_fields: false, before_after_photos_state: 'inactive', address_name: properties[0]?.address_name }
      ];
    }
  }

  getUpsellsMockData(accountType?: AccountType): any[] {
    const type = accountType || this.getAccountType();
    const today = new Date();
    const properties = this.getPropertiesMockData(type);
    if (type === 'rental') {
      // Reservation IDs map to months (ID % 12): 0=Jan, 1=Feb, 2=Mar, 3=Apr, 4=May, 5=Jun
      return [
        this.createUpsellObject(1, 'Early Check-in Request', 'early_checkin', 'Pending', this.addDays(today, -1), properties[0]?.id, 0),
        this.createUpsellObject(2, 'Late Check-out Request', 'late_checkout', 'Approved', this.addDays(today, -2), properties[1]?.id, 1),
        this.createUpsellObject(3, 'Midstay Cleaning Request', 'midstay_cleaning', 'Pending', this.addDays(today, -3), properties[2]?.id, 2),
        this.createUpsellObject(4, 'Early Check-in Request', 'early_checkin', 'Denied', this.addDays(today, -4), properties[3]?.id, 3),
        this.createUpsellObject(5, 'Late Check-out Request', 'late_checkout', 'Completed', this.addDays(today, -5), properties[4]?.id, 4),
        this.createUpsellObject(6, 'Pet Cleaning Add-on', 'pet_cleaning', 'Approved', this.addDays(today, -6), properties[5]?.id, 5)
      ];
    } else if (type === 'company') {
      return [
        this.createUpsellObject(1, 'After-Event Cleanup', 'event_cleanup', 'Pending', this.addDays(today, -1), properties[0]?.id, 0),
        this.createUpsellObject(2, 'Carpet Deep Cleaning', 'carpet_cleaning', 'Approved', this.addDays(today, -3), properties[1]?.id, 1),
        this.createUpsellObject(3, 'Window Washing', 'window_cleaning', 'Completed', this.addDays(today, -7), properties[0]?.id, 2),
        this.createUpsellObject(4, 'Floor Waxing', 'floor_waxing', 'Pending', this.addDays(today, -10), properties[1]?.id, 3)
      ];
    } else {
      return [
        this.createUpsellObject(1, 'Deep Cleaning Upgrade', 'deep_cleaning', 'Pending', this.addDays(today, -1), properties[0]?.id, 0),
        this.createUpsellObject(2, 'Appliance Cleaning Add-on', 'appliance_cleaning', 'Approved', this.addDays(today, -7), properties[0]?.id, 1),
        this.createUpsellObject(3, 'Garage Organization', 'garage_organization', 'Completed', this.addDays(today, -14), properties[0]?.id, 2),
        this.createUpsellObject(4, 'Post-Party Cleanup', 'party_cleanup', 'Pending', this.addDays(today, -21), properties[0]?.id, 3)
      ];
    }
  }

  private createUpsellObject(index: number, subject: string, requestType: string, status: string, date: Date, propertyId: number, reservationId: number): any {
    return {
      id: index,
      uuid: `upsell-${String(index).padStart(3, '0')}`,
      state: 'active',
      metadata: {
        request_data: {
          body: `property: ${propertyId}\nreservation: ${reservationId}\nrequestType: ${requestType}`
        }
      },
      components: [{
        value: {
          subject,
          status,
          last_update_at: date.toISOString()
        }
      }],
      header_text: subject
    };
  }

  getThreadMessagesMockData(accountType?: AccountType): any[] {
    const type = accountType || this.getAccountType();
    if (type === 'rental') {
      return this.generateRentalThreadMessages();
    } else if (type === 'company') {
      return this.generateCompanyThreadMessages();
    } else {
      return this.generateRegularThreadMessages();
    }
  }

  private generateRentalThreadMessages(): any[] {
    const today = new Date();
    const pros = this.getMyProsMockData('rental');
    const pro = pros[0];
    const proFullName = `${pro.first_name} ${pro.last_name}`;
    const messages: any[] = [];
    let msgId = 800000;
    const addMessage = (minutesAgo: number, fromType: 'Homekeeper' | 'CustomerMember', text: string) => {
      const sentAt = new Date(today.getTime() - minutesAgo * 60000);
      messages.push({
        id: msgId++,
        sent_at: sentAt.toISOString(),
        from_member: fromType === 'Homekeeper'
          ? { type: 'Homekeeper', first_name: proFullName, last_name: '' }
          : { type: 'CustomerMember', first_name: 'Riley Frederickson', last_name: '' },
        content: { text, files: [] }
      });
    };
    addMessage(1440, 'CustomerMember', "Hi Aurelia! Just wanted to confirm you're still good for the Beach Bungalow turnover today?");
    addMessage(1430, 'Homekeeper', "Good morning! Yes, I'm all set. I have City View Condo first at 11am, then Beach Bungalow after that.");
    addMessage(1420, 'CustomerMember', "Perfect! The City View guests checked out on time. Beach Bungalow checkout is at 11am, so you should be fine timing-wise.");
    addMessage(1410, 'Homekeeper', "Great! What time do the new guests check in at Beach Bungalow?");
    addMessage(1400, 'CustomerMember', "They're arriving at 4pm. Plenty of buffer time.");
    addMessage(1390, 'Homekeeper', "Perfect. I'll head to City View first and text you when I'm on my way to Beach Bungalow.");
    addMessage(240, 'Homekeeper', "Just arrived at City View! Starting the turnover now. Place looks pretty clean which is nice.");
    addMessage(220, 'CustomerMember', "Awesome! Let me know if you need anything.");
    addMessage(200, 'Homekeeper', "Quick question - the coffee maker looks pretty gunked up. Should I run a cleaning cycle?");
    addMessage(190, 'CustomerMember', "Yes please! There are descaling tablets under the sink.");
    addMessage(180, 'Homekeeper', "Found them! Also noticed the Keurig pods are low - only 3 left.");
    addMessage(170, 'CustomerMember', "I'll add that to the supply order. Thanks for catching that!");
    addMessage(150, 'Homekeeper', "Kitchen is done. Working on the bedrooms now. The anniversary couple is going to love it!");
    addMessage(140, 'CustomerMember', "You remembered! Thanks for doing the special towel fold for them.");
    addMessage(130, 'Homekeeper', "Of course! I also left some welcome chocolates on the nightstand.");
    addMessage(120, 'CustomerMember', "You're the best! How's it looking timeline-wise for Beach Bungalow?");
    addMessage(110, 'Homekeeper', "Should be wrapping up City View in about 30 minutes. I'll head straight to Beach Bungalow after.");
    addMessage(90, 'Homekeeper', "Almost done here! Just finishing up the bathrooms. Found a small water stain on the ceiling - I'll send you a photo.");
    addMessage(80, 'CustomerMember', "Oh no, thanks for catching that. I'll contact the building manager.");
    addMessage(70, 'Homekeeper', "Photo sent to your email. It's not dripping but worth checking soon.");
    addMessage(60, 'CustomerMember', "Got it, thanks! Just emailed maintenance.");
    addMessage(50, 'Homekeeper', "City View is all done! Place looks amazing. Heading to my car now.");
    addMessage(40, 'CustomerMember', "Amazing work as always! The guests should arrive around 3:30. Everything ready for early access?");
    addMessage(30, 'Homekeeper', "Yep! Left the key in the lockbox and AC is set to 72. They're all set.");
    addMessage(20, 'CustomerMember', "Perfect. Let me know when you get to Beach Bungalow!");
    addMessage(15, 'Homekeeper', "Will do! Just got in the car. Should be there in about 15 minutes.");
    addMessage(10, 'CustomerMember', "Sounds good. The previous guests had a late checkout request - they might still be packing up when you arrive.");
    addMessage(5, 'Homekeeper', "Oh okay, no problem. I'll text them when I'm close.");
    addMessage(1, 'Homekeeper', "Heads up - I'm running about 20 min behind for the Beach Bungalow turnover. Guest checkout was late. Should still have it ready by 3pm.");
    return messages;
  }

  private generateRegularThreadMessages(): any[] {
    const today = new Date();
    const pros = this.getMyProsMockData('regular');
    const pro = pros[0];
    const proFullName = `${pro.first_name} ${pro.last_name}`;
    const messages: any[] = [];
    let msgId = 900000;
    const addMessage = (minutesAgo: number, fromType: 'Homekeeper' | 'CustomerMember', text: string) => {
      const sentAt = new Date(today.getTime() - minutesAgo * 60000);
      messages.push({
        id: msgId++,
        sent_at: sentAt.toISOString(),
        from_member: fromType === 'Homekeeper'
          ? { type: 'Homekeeper', first_name: proFullName, last_name: '' }
          : { type: 'CustomerMember', first_name: 'Riley Frederickson', last_name: '' },
        content: { text, files: [] }
      });
    };
    addMessage(10080, 'CustomerMember', "Hi Maria! I saw your ad online. Are you available for weekly cleanings?");
    addMessage(10020, 'Homekeeper', "Hi! Yes, I have availability. What day works best for you?");
    addMessage(9960, 'CustomerMember', "Thursdays would be ideal if possible. Around 10am?");
    addMessage(9900, 'Homekeeper', "Thursday at 10am works great! Can you tell me a bit about your home?");
    addMessage(9840, 'CustomerMember', "It's a 3 bedroom, 2 bath house. About 1,800 square feet. We have a dog too - hope that's okay!");
    addMessage(9780, 'Homekeeper', "I love dogs! What's his name? And 2.5 hours should be perfect for your home.");
    addMessage(9720, 'CustomerMember', "His name is Max! He's super friendly. And that sounds great. What's your rate?");
    addMessage(9660, 'Homekeeper', "$85 for a 2.5 hour cleaning. I bring all my own supplies.");
    addMessage(9600, 'CustomerMember', "That works for us! Can we start this Thursday?");
    addMessage(9540, 'Homekeeper', "Perfect! I'll see you Thursday at 10am. What's your address?");
    addMessage(4320, 'Homekeeper', "Good morning! I'm here and getting started. Max greeted me at the door 🐕");
    addMessage(4260, 'CustomerMember', "Haha he loves you already! Let me know if you need anything.");
    addMessage(4200, 'Homekeeper', "Quick question - I noticed you have a new rug in the living room. Is it okay to vacuum it?");
    addMessage(4140, 'CustomerMember', "Good catch! It's wool so maybe just a light vacuum on the gentle setting.");
    addMessage(4080, 'Homekeeper', "Got it, I'll be gentle. It's beautiful by the way!");
    addMessage(2880, 'Homekeeper', "Bathrooms are done and looking great. Working on the bedrooms now.");
    addMessage(2820, 'CustomerMember', "You're amazing, thank you!");
    addMessage(2700, 'Homekeeper', "Found a ring under the couch cushion! Left it on the kitchen counter by the fruit bowl.");
    addMessage(2640, 'CustomerMember', "Oh my gosh! That's my mom's ring - she couldn't find it anywhere. Thank you!!");
    addMessage(2580, 'Homekeeper', "So glad I found it! She must have been worried sick.");
    addMessage(2520, 'CustomerMember', "She was! You just made her whole week.");
    addMessage(2400, 'Homekeeper', "All done! Everything is sparkling. Also noticed you're running low on paper towels.");
    addMessage(2340, 'CustomerMember', "Thanks for the heads up! I'll add those to my shopping list.");
    addMessage(2280, 'Homekeeper', "Left my invoice on the counter. See you next week!");
    addMessage(1440, 'CustomerMember', "Hey Maria, quick question - any chance you could come on Thursday next week instead of Wednesday? We have workers coming.");
    addMessage(1380, 'Homekeeper', "Thursday works great for me! Same time, 10am?");
    addMessage(1320, 'CustomerMember', "Yes please. There might be extra dust from the repairs. Would you be able to do extra dusting?");
    addMessage(1260, 'Homekeeper', "Absolutely, I'll bring my HEPA filter attachment. What kind of repairs?");
    addMessage(1200, 'CustomerMember', "Drywall repair in the hallway and some painting.");
    addMessage(1140, 'Homekeeper', "Good to know. I've dealt with post-construction dust before - no problem!");
    addMessage(1080, 'CustomerMember', "You're a lifesaver. Thanks Maria!");
    addMessage(1020, 'Homekeeper', "Happy to help! See you Thursday at 10am. Have a great week!");
    addMessage(1, 'Homekeeper', "Hi! Just confirming our cleaning tomorrow at 10am. Any specific areas you'd like me to focus on this time?");
    return messages;
  }

  private generateCompanyThreadMessages(): any[] {
    const today = new Date();
    const pros = this.getMyProsMockData('company');
    const pro = pros[0];
    const proFullName = `${pro.first_name} ${pro.last_name}`;
    const messages: any[] = [];
    let msgId = 950000;
    const addMessage = (minutesAgo: number, fromType: 'Homekeeper' | 'CustomerMember', text: string) => {
      const sentAt = new Date(today.getTime() - minutesAgo * 60000);
      messages.push({
        id: msgId++,
        sent_at: sentAt.toISOString(),
        from_member: fromType === 'Homekeeper'
          ? { type: 'Homekeeper', first_name: proFullName, last_name: '' }
          : { type: 'CustomerMember', first_name: 'Riley Frederickson', last_name: '' },
        content: { text, files: [] }
      });
    };
    addMessage(4320, 'CustomerMember', "Hi! We have a board meeting tomorrow at 9am in the main conference room. Can your team do an extra thorough clean of that room tonight?");
    addMessage(4260, 'Homekeeper', "Absolutely! We'll make sure it's spotless. Any specific requests for the setup?");
    addMessage(4200, 'CustomerMember', "Yes please make sure all the chairs are arranged around the table, whiteboard is clean, and maybe wipe down the video conference equipment.");
    addMessage(4140, 'Homekeeper', "Got it. We'll also make sure the glass walls are streak-free. Those always look good for important meetings.");
    addMessage(4080, 'CustomerMember', "Perfect, thank you!");
    addMessage(2880, 'Homekeeper', "Just finished the conference room. It looks great! We arranged 14 chairs as usual - is that the right number?");
    addMessage(2820, 'CustomerMember', "Actually we'll have 16 people. Can you add 2 more chairs from the storage room?");
    addMessage(2760, 'Homekeeper', "Done! 16 chairs set up. Also restocked the water glasses and coffee supplies on the credenza.");
    addMessage(2700, 'CustomerMember', "You're amazing. Thank you!");
    addMessage(1440, 'Homekeeper', "Good evening! We're here for tonight's cleaning. Quick question - there are some boxes in the break room. Should we clean around them?");
    addMessage(1380, 'CustomerMember', "Those are supplies being delivered tomorrow. Please just clean around them for now.");
    addMessage(1320, 'Homekeeper', "No problem. Also noticed the restroom on the 2nd floor is almost out of paper towels. Should I restock?");
    addMessage(1260, 'CustomerMember', "Yes please! Thanks for catching that.");
    addMessage(1200, 'Homekeeper', "Done! Also topped up the hand soap and sanitizer while I was at it.");
    addMessage(1140, 'Homekeeper', "The break room fridge has some items that look pretty old. Want us to clear out anything expired?");
    addMessage(1080, 'CustomerMember', "Yes please! Anything older than a week can go.");
    addMessage(1020, 'Homekeeper', "Cleared out about 10 containers. Fridge looks much better now. Also wiped down all the shelves.");
    addMessage(960, 'CustomerMember', "Perfect. That's been on my list forever. Thank you!");
    addMessage(720, 'Homekeeper', "One thing to flag - the carpet near the copy room has a stain that won't come out with regular cleaning.");
    addMessage(660, 'CustomerMember', "Good to know. Can you get me a quote for professional cleaning?");
    addMessage(600, 'Homekeeper', "Absolutely. I'll have it for you by end of week.");
    addMessage(360, 'Homekeeper', "All done for tonight! Floors mopped, trash emptied, all restrooms restocked.");
    addMessage(300, 'CustomerMember', "Wonderful! What time did you lock up?");
    addMessage(240, 'Homekeeper', "9:15pm. Set the alarm and made sure all doors were secured. See you Wednesday!");
    addMessage(180, 'CustomerMember', "Great work as always. Thank you!");
    addMessage(120, 'Homekeeper', "Happy to help! Let me know if you need anything before then.");
    addMessage(1, 'Homekeeper', "Office cleaning complete at Main Office. Vacuumed all floors, emptied trash, and sanitized all common areas. Locked up at 9pm.");
    return messages;
  }

  /**
   * Realistic Palm Springs short-term-rental mock for the Overview / Property
   * P&L page. Used as a fallback while the about-this-page overlay is up so
   * the page looks populated for new accounts instead of $0 / empty.
   *
   * All amounts are signed integer cents (matches the BE convention).
   * Expense values are negative; net_income is the synthetic row the BE
   * appends to finance_summary.
   */
  getStrategyMockData(): StrategyMockData {
    // Year-total projected baselines (in cents). Actuals outperform — they
    // start a touch below in early months, chop around, then break clearly
    // above as the year progresses.
    const projectedGrossYear = 56_000_000;
    const projectedOpexYear  = -42_000_000;

    // Build month entries. Step index 0 = 11 months ago, 11 = current month.
    // Underlying step weight grows from ~0.55 → ~1.55 (overall up-and-to-the-
    // right trend), then a per-month jitter sequence adds visible randomness
    // so the projected bars don't look mechanically monotonic. The jitter is
    // applied to BOTH projected and actual so the two curves move together.
    // Gentle underlying climb (~0.92 → ~1.14) so the year is only *barely*
    // up-and-to-the-right, plus a large per-month jitter so the bars look
    // choppy/volatile month to month rather than a clean ramp.
    const baseWeight = (step: number) => 0.92 + (step / 11) * 0.22;
    const monthJitter = [+0.00, +0.30, -0.24, +0.20, -0.30, +0.26, -0.14, +0.32, -0.28, +0.18, -0.22, +0.12];
    const stepWeight = (step: number) => Math.max(0.30, baseWeight(step) + monthJitter[step]);
    const totalWeight = Array.from({ length: 12 }, (_, s) => stepWeight(s))
      .reduce((a, b) => a + b, 0);

    // Additional outperform chop on actuals only (larger swing = choppier).
    const chopSeq = [+0.090, -0.120, +0.070, -0.060, +0.110, -0.050];
    const today = new Date();
    const trendMonths: FinanceTrendsMonth[] = [];
    let grossRevenueCents = 0;
    let operatingExpensesCents = 0;
    for (let step = 0; step < 12; step++) {
      const i = 11 - step;
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const month = d.getMonth() + 1;
      const year = d.getFullYear();
      const w = stepWeight(step);

      const projectedRev  = Math.round((projectedGrossYear * w) / totalWeight);
      const projectedOpex = Math.round((projectedOpexYear  * w) / totalWeight);

      // Revenue ratio drifts only slightly upward (-0.03 → +0.05) so actuals
      // hug the projection with visible chop instead of breaking clearly above.
      const trend = -0.03 + (step / 11) * 0.08;
      const chop = chopSeq[step % chopSeq.length];
      const revPerf = trend + chop;
      // Opex outperforms in mirror: early slightly over, late modestly under.
      const opexPerf = 0.03 - (step / 11) * 0.07 + (chop * 0.5);

      const actualRev  = Math.round(projectedRev  * (1 + revPerf));
      const actualOpex = Math.round(projectedOpex * (1 + opexPerf));

      grossRevenueCents      += actualRev;
      operatingExpensesCents += actualOpex;

      trendMonths.push({
        year,
        month,
        actual_revenue: actualRev,
        projected_revenue: projectedRev,
        actual_net_income: actualRev + actualOpex,
        projected_net_income: projectedRev + projectedOpex,
      });
    }
    const trends: FinanceTrendsData = { months: trendMonths };

    // KPI tiles — totals derived from the (outperforming) monthly actuals so
    // the cards stay consistent with the chart.
    const netOperatingIncomeCents = grossRevenueCents + operatingExpensesCents;
    const kpis: FinanceKpisData = {
      gross_revenue: grossRevenueCents,
      operating_expenses: operatingExpensesCents,
      net_operating_income: netOperatingIncomeCents,
      occupancy: 67.2,
      avg_nightly_rate: 219_800,
    };

    // Operational KPIs — agents enabled and TIDY answering inquiries so the response-time tiles render in the demo.
    const opsKpis: OperationsKpisData = {
      avg_star_rating: 4.8,
      turnover_completion_rate: 96.5,
      account_guest_response_time_minutes: 12,
      account_homekeeper_response_time_minutes: 34,
      guest_agent_enabled: true,
      homekeeper_agent_enabled: true,
      inquiry_response_time_minutes: 18,
      inquiries_answered_by_tidy: true,
    };

    // Summary table rows — six subcategories + synthetic Net Income row.
    const buildRow = (
      subcategory: string,
      label: string,
      actual: number,
      projected: number,
    ): FinanceSummaryRow => {
      const variance = actual - projected;
      const variancePct = projected === 0
        ? null
        : Math.round((variance / Math.abs(projected)) * 1000) / 10;
      return { subcategory, label, actual, projected, variance, variance_percent: variancePct };
    };

    // Summary numbers tell the same "outperforming" story as the chart:
    // income subcategories beat their projections, expense subcategories
    // come in under (less negative than) their projections.
    const projectedNetIncomeCents = projectedGrossYear + projectedOpexYear;
    const summary: FinanceSummaryData = {
      rows: [
        // Income subcategories (positive cents) — actual > projected.
        buildRow('rental_income',      'Rental Income',         48_400_000, 45_000_000),
        buildRow('guest_tenant_fees',  'Guest & Tenant Fees',   10_120_000,  9_100_000),
        buildRow('other_income',       'Other Income',           2_180_000,  1_900_000),
        // Operating-expense subcategories (negative cents) — actual less negative than projected.
        buildRow('management_platform_fees', 'Management & Platform Fees',  -9_780_000, -10_500_000),
        buildRow('cleaning_turnover',        'Cleaning & Turnover',         -7_820_000,  -8_100_000),
        buildRow('repairs_maintenance',      'Repairs & Maintenance',       -6_950_000,  -7_400_000),
        buildRow('utilities',                'Utilities',                   -4_780_000,  -5_050_000),
        buildRow('insurance',                'Insurance',                   -3_960_000,  -3_960_000),
        buildRow('taxes_government_fees',    'Taxes & Government Fees',     -6_640_000,  -6_990_000),
        // Synthetic Net Income row (matches BE convention).
        buildRow('net_income',         'Net Income',            netOperatingIncomeCents, projectedNetIncomeCents),
      ],
    };

    // AI-style "Suggestions to Improve" cards — revenue upside (positive) and
    // cost savings (negative) a property manager would act on.
    const suggestions: StrategySuggestion[] = [
      {
        icon: 'assets/svg/trending-up-outline.svg',
        title: 'Raise weekday rates',
        body: 'Weekday occupancy is averaging 88%. A 5–8% nightly bump could capture more revenue without hurting bookings.',
        impact: 'positive',
        metric: '+$2,400/mo',
      },
      {
        icon: 'assets/svg/calendar-outline.svg',
        title: 'Fill orphan gap nights',
        body: 'Several 1–2 night gaps are open next month. Auto-applying a small gap-night discount typically fills about half.',
        impact: 'positive',
        metric: '+$1,950/mo',
      },
      {
        icon: 'assets/svg/pricetag-outline.svg',
        title: 'Offer mid-stay cleans',
        body: 'Guests on 5+ night stays accept a mid-stay clean upsell about a third of the time when it is offered at booking.',
        impact: 'positive',
        metric: '+$680/mo',
      },
      {
        icon: 'assets/svg/construct-outline.svg',
        title: 'Consolidate cleaning vendors',
        body: 'Cleaning & turnover is running ~14% above comparable portfolios. Consolidating vendors trims your largest variable cost.',
        impact: 'negative',
        metric: '−$1,100/mo',
      },
      {
        icon: 'assets/svg/flash-outline.svg',
        title: 'Trim rising utilities',
        body: 'Electricity is trending ~18% above last year. A smart-thermostat schedule between stays cuts idle HVAC cost.',
        impact: 'negative',
        metric: '−$240/mo',
      },
    ];

    return { kpis, trends, summary, suggestions, opsKpis };
  }

  /**
   * Realistic Palm Springs short-term-rental mock for the Monthly P&L grid.
   * Used as a fallback while the about-this-page overlay is up so the grid
   * looks populated for new accounts.
   *
   * Returns 12 months of values for `year` (defaults to the current year)
   * across a representative slice of income + expense subcategories. All
   * amounts are signed integer cents (expenses negative). A couple of cells
   * carry an `overridden_amount` so the "manual" cell styling demos.
   */
  getMonthlyPLMockData(year: number = new Date().getFullYear()): MonthlyFinanceProfitAndLoss {
    // Growing month weights so each row trends upward across the year.
    const stepWeight = (m: number) => 0.7 + (m / 11) * 0.6; // Jan=0.70 → Dec=1.30
    const totalWeight = Array.from({ length: 12 }, (_, m) => stepWeight(m))
      .reduce((a, b) => a + b, 0);

    let idCounter = 1;
    const buildItem = (
      key: string,
      label: string,
      yearTotalCents: number,
      overriddenMonths: Record<number, number> = {},
    ): MonthlyFinanceItem => {
      const values: MonthlyFinanceMonthValue[] = [];
      for (let m = 1; m <= 12; m++) {
        const w = stepWeight(m - 1);
        const computed = Math.round((yearTotalCents * w) / totalWeight);
        const overridden = overriddenMonths[m] ?? null;
        values.push({
          year,
          month: m,
          computed_amount: computed,
          overridden_amount: overridden,
          effective_amount: overridden ?? computed,
        });
      }
      return { id: idCounter++, key, label, values };
    };

    // Every BE-seeded line item gets a non-zero year total so the grid is
    // never blank when a subcategory is expanded. STR-relevant items get the
    // large amounts; less-relevant items get small "trickle" amounts.
    const subRentalIncome: MonthlyFinanceSubcategory = {
      key: 'rental_income',
      label: 'Rental Income',
      items: [
        buildItem('base_rent',                  'Base Rent / Long-Term Rent',           360_000),
        buildItem('section_8_rent',             'Section 8 / Subsidized Rent',           60_000),
        buildItem('short_term_nightly_revenue', 'Short-Term Nightly Revenue',        42_000_000, { 5: 4_250_000 }),
        buildItem('furnished_corporate_premium','Furnished / Corporate Rental Premium', 600_000),
        buildItem('pet_rent',                   'Pet Rent (recurring)',                 480_000),
        buildItem('parking_storage_rent',       'Parking & Storage Rent',               180_000),
      ],
    };
    const subGuestFees: MonthlyFinanceSubcategory = {
      key: 'guest_tenant_fees',
      label: 'Guest & Tenant Fees',
      items: [
        buildItem('cleaning_fees_collected',   'Cleaning Fees Collected from Guests', 7_800_000),
        buildItem('resort_amenity_fees',       'Resort / Amenity Fees',               1_200_000),
        buildItem('guest_service_upsells',     'Guest Service Upsells',                 540_000),
        buildItem('pet_fees_one_time',         'Pet Fees (one-time)',                   320_000),
        buildItem('late_fees',                 'Late Fees',                              48_000),
        buildItem('application_fees',          'Application Fees',                       24_000),
        buildItem('activity_equipment_income', 'Activity & Equipment Income',           180_000),
        buildItem('cancellation_damage_waiver','Cancellation / Damage Waiver Income',   360_000),
        buildItem('lease_termination_fees',    'Lease Termination Fees',                 18_000),
        buildItem('nsf_returned_payment_fees', 'NSF / Returned Payment Fees',            12_000),
      ],
    };
    const subOtherIncome: MonthlyFinanceSubcategory = {
      key: 'other_income',
      label: 'Other Income',
      items: [
        buildItem('laundry_vending',              'Laundry / Vending',                            240_000),
        buildItem('tenant_utility_reimbursements','Tenant Utility Reimbursements (Pass-Throughs)', 90_000),
        buildItem('cam_reimbursements',           'CAM / Common Area Reimbursements',              60_000),
        buildItem('recoveries',                   'Recoveries (deposits, insurance claims)',      120_000),
        buildItem('other_misc_income',            'Other Misc Income',                            140_000),
      ],
    };
    const subContraRevenue: MonthlyFinanceSubcategory = {
      key: 'contra_revenue',
      label: 'Contra-Revenue',
      items: [
        // Negative because contra-revenue reduces income (BE sign: -1).
        buildItem('contra_revenue', 'Contra-Revenue', -420_000),
      ],
    };
    const incomeCategory: MonthlyFinanceCategory = {
      key: 'income',
      label: 'Income',
      subcategories: [subRentalIncome, subGuestFees, subOtherIncome, subContraRevenue],
    };

    const subMgmt: MonthlyFinanceSubcategory = {
      key: 'management_platform_fees',
      label: 'Management & Platform Fees',
      items: [
        buildItem('property_management_fee', 'Property Management Fee',                          -4_800_000),
        buildItem('leasing_commission',      'Leasing Commission',                                  -90_000),
        buildItem('channel_ota_fees',        'Channel / OTA Fees (Airbnb, Vrbo, Booking.com)',   -4_950_000),
        buildItem('payment_processing_fees', 'Payment Processing / Merchant Fees',                 -780_000),
      ],
    };
    const subCleaning: MonthlyFinanceSubcategory = {
      key: 'cleaning_turnover',
      label: 'Cleaning & Turnover',
      items: [
        buildItem('cleaning_service',        'Cleaning Service',          -6_240_000),
        buildItem('linens_laundry_service',  'Linens & Laundry Service',  -1_080_000, { 3: -125_000 }),
        buildItem('cleaning_supplies',       'Cleaning Supplies',           -480_000),
      ],
    };
    const subGuestSupplies: MonthlyFinanceSubcategory = {
      key: 'guest_supplies_consumables',
      label: 'Guest Supplies & Consumables',
      items: [
        buildItem('consumables',       'Consumables (toiletries, paper, pantry)', -720_000),
        buildItem('welcome_gifts',     'Welcome Gifts / Amenities',               -360_000),
        buildItem('replacement_items', 'Replacement Items (dishware, decor)',     -300_000),
        buildItem('other_consumables', 'Other Consumables',                       -120_000),
      ],
    };
    const subRepairs: MonthlyFinanceSubcategory = {
      key: 'repairs_maintenance',
      label: 'Repairs & Maintenance',
      items: [
        buildItem('general_repairs',     'General Repairs / Handyman',                       -3_600_000),
        buildItem('plumbing_repairs',    'Plumbing Repairs',                                   -480_000),
        buildItem('electrical_repairs',  'Electrical Repairs',                                 -360_000),
        buildItem('hvac_service',        'HVAC Service & Repairs',                           -1_800_000),
        buildItem('appliance_repairs',   'Appliance Repairs',                                  -240_000),
        buildItem('roof_repairs',        'Roof Repairs',                                       -180_000),
        buildItem('interior_repairs',    'Interior Repairs (paint, flooring, doors, locks)',   -720_000),
        buildItem('pest_control',        'Pest Control',                                       -360_000),
        buildItem('landscaping',         'Landscaping & Lawn Care',                            -960_000),
        buildItem('snow_removal',        'Snow Removal',                                        -24_000),
        buildItem('pool_spa_hot_tub',    'Pool, Spa & Hot Tub',                              -1_440_000),
        buildItem('maintenance_supplies','Maintenance Supplies',                               -180_000),
      ],
    };
    const subUtilities: MonthlyFinanceSubcategory = {
      key: 'utilities',
      label: 'Utilities',
      items: [
        buildItem('electric',           'Electric',                       -2_880_000),
        buildItem('gas_propane',        'Gas / Propane / Heating Oil',      -480_000),
        buildItem('water_sewer',        'Water & Sewer',                    -720_000),
        buildItem('trash_recycling',    'Trash & Recycling',                -180_000),
        buildItem('internet_streaming', 'Internet & Streaming',             -480_000),
        buildItem('phone_landline',     'Phone / Landline',                 -120_000),
      ],
    };
    const subInsurance: MonthlyFinanceSubcategory = {
      key: 'insurance',
      label: 'Insurance',
      items: [
        buildItem('property_insurance',     'Property Insurance (dwelling / STR policy)', -3_960_000),
        buildItem('catastrophic_insurance', 'Catastrophic / Hazard Insurance',              -720_000),
        buildItem('liability_umbrella',     'Liability / Umbrella Policy',                  -480_000),
      ],
    };
    const subTaxes: MonthlyFinanceSubcategory = {
      key: 'taxes_government_fees',
      label: 'Taxes & Government Fees',
      items: [
        buildItem('property_tax',      'Property Tax (incl. special assessments)', -4_800_000),
        buildItem('occupancy_tot_tax', 'Short-Term Occupancy / TOT Tax Remitted',  -1_680_000),
        buildItem('city_business_tax', 'City / Local Business Tax',                  -240_000),
        buildItem('str_permit_fees',   'STR Permit / License Fees',                  -180_000),
        buildItem('hoa_dues',          'HOA Dues & Assessments',                     -480_000),
      ],
    };
    const subMarketing: MonthlyFinanceSubcategory = {
      key: 'marketing_advertising',
      label: 'Marketing & Advertising',
      items: [
        buildItem('listing_distribution_seo',  'Listing Distribution & SEO',           -360_000),
        buildItem('listing_media',             'Listing Media (photo, video, drone)',  -240_000),
        buildItem('staging_physical_marketing','Staging & Physical Marketing',         -180_000),
        buildItem('direct_booking_site',       'Direct Booking Site (domain, hosting)', -90_000),
        buildItem('digital_marketing',         'Digital Marketing (ads, social, email)',-720_000),
        buildItem('other_advertising',         'Other Advertising',                     -60_000),
      ],
    };
    const subTenantAcq: MonthlyFinanceSubcategory = {
      key: 'tenant_acquisition_leasing',
      label: 'Tenant Acquisition & Leasing',
      items: [
        buildItem('tenant_find_fee',  'Tenant Find / Leasing Service Fee',         -120_000),
        buildItem('tenant_screening', 'Tenant Screening',                           -48_000),
        buildItem('lease_execution',  'Lease Execution & Onboarding',               -36_000),
        buildItem('inspections',      'Inspections (move-in, move-out, annual)',   -240_000),
      ],
    };
    const subAdmin: MonthlyFinanceSubcategory = {
      key: 'admin_office',
      label: 'Admin & Office',
      items: [
        buildItem('general_admin',       'General Admin (office, postage, bank fees)', -480_000),
        buildItem('eviction_court_fees', 'Eviction / Court Fees',                       -24_000),
        buildItem('travel_mileage',      'Travel & Mileage',                           -360_000),
      ],
    };
    const expenseCategory: MonthlyFinanceCategory = {
      key: 'operating_expenses',
      label: 'Operating Expenses',
      subcategories: [
        subMgmt, subCleaning, subGuestSupplies, subRepairs, subUtilities,
        subInsurance, subTaxes, subMarketing, subTenantAcq, subAdmin,
      ],
    };

    return { categories: [incomeCategory, expenseCategory] };
  }

}
